<script setup>
import camelcaseKeys from 'camelcase-keys';
import { debounce } from '@chatwoot/utils';
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue';
import { format, parseISO } from 'date-fns';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';

import ConversationAPI from 'dashboard/api/conversations';
import ContactAPI from 'dashboard/api/contacts';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';
import CreateNewContactDialog from 'dashboard/components-next/Contacts/ContactsForm/CreateNewContactDialog.vue';
import ProductOrderDetailDialog from './components/ProductOrderDetailDialog.vue';
import {
  buildTemplateParameters,
  COMPONENT_TYPES,
  findComponentByType,
  MEDIA_FORMATS,
} from 'dashboard/helper/templateHelper';
import {
  resolveProductsOrdersConfig,
  resolveProductsOrdersFeatures,
} from 'dashboard/helper/productsOrdersFeatures';

const store = useStore();
const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { updateAccount } = useAccount();
const STANDARD_ORDER_FLOW = ['pending', 'in_progress', 'completed'];
const ADVANCED_ORDER_FLOW = [
  'pending',
  'confirmed',
  'preparing',
  'shipped',
  'delivered',
];
const AUTOMATION_WEBHOOK_EVENTS = [
  'created',
  'updated',
  'status_changed',
  'completed',
  'canceled',
  'stuck',
];

const products = useMapGetter('productsOrders/getProducts');
const productOrders = useMapGetter('productsOrders/getProductOrders');
const uiFlags = useMapGetter('productsOrders/getUIFlags');
const inboxes = useMapGetter('inboxes/getInboxes');
const agents = useMapGetter('agents/getAgents');

const currentAccount = computed(() =>
  store.getters['accounts/getAccount'](Number(route.params.accountId))
);
const productsOrdersFeatures = computed(() =>
  resolveProductsOrdersFeatures(currentAccount.value?.settings)
);
const productsOrdersConfig = computed(() =>
  resolveProductsOrdersConfig(currentAccount.value?.settings)
);
const orderFlowStatuses = computed(() =>
  productsOrdersFeatures.value.advancedStatuses
    ? ADVANCED_ORDER_FLOW
    : STANDARD_ORDER_FLOW
);

const activeTab = ref('orders');
const ordersView = ref('list');
const productDialogRef = ref(null);
const orderDialogRef = ref(null);
const orderDetailDialogRef = ref(null);
const executionPreviewDialogRef = ref(null);
const confirmationDialogRef = ref(null);
const settingsDialogRef = ref(null);
const orderQuickContactDialogRef = ref(null);
const editingProductId = ref(null);
const editingOrderId = ref(null);
const deletedActionIds = ref([]);
const localActionKey = ref(0);
const localVariantKey = ref(0);
const localConfigActionKey = ref(0);
const localStuckRuleKey = ref(0);
const templateParserRefs = ref({});
const linkedConversation = ref(null);
const conversationContextError = ref('');
const isFetchingConversationContext = ref(false);
const lastResolvedConversationId = ref(null);
const conversationContextRequestToken = ref(0);
const suppressConversationContextWatch = ref(false);
const handledConversationPrefillKey = ref('');
const selectedOrderContactId = ref('');
const orderContactOptions = ref([]);
const isSearchingOrderContacts = ref(false);
const orderContactsRequestController = ref(null);
const isSavingModuleConfig = ref(false);
const draggedOrderId = ref(null);
const hoveredKanbanColumn = ref('');
const pendingExecution = ref({
  order: null,
  action: null,
});
const pendingConfirmation = ref({
  title: '',
  message: '',
  confirmLabel: '',
  tone: 'alert',
  action: null,
});
const orderFilters = reactive({
  search: '',
  status: 'all',
  productId: 'all',
  dateFrom: '',
  dateTo: '',
});

const nextActionKey = () => {
  localActionKey.value += 1;
  return `action-${localActionKey.value}`;
};

const nextVariantKey = () => {
  localVariantKey.value += 1;
  return `variant-${localVariantKey.value}`;
};

const nextConfigActionKey = () => {
  localConfigActionKey.value += 1;
  return `config-action-${localConfigActionKey.value}`;
};

const nextStuckRuleKey = () => {
  localStuckRuleKey.value += 1;
  return `stuck-rule-${localStuckRuleKey.value}`;
};

const openConfirmationDialog = ({
  title,
  message,
  confirmLabel,
  tone = 'alert',
  action,
}) => {
  pendingConfirmation.value = {
    title,
    message,
    confirmLabel,
    tone,
    action,
  };
  confirmationDialogRef.value?.open?.();
};

const resetConfirmationDialog = () => {
  pendingConfirmation.value = {
    title: '',
    message: '',
    confirmLabel: '',
    tone: 'alert',
    action: null,
  };
};

const closeConfirmationDialog = () => {
  resetConfirmationDialog();
  confirmationDialogRef.value?.close?.();
};

const confirmPendingAction = async () => {
  const action = pendingConfirmation.value.action;
  if (!action) {
    return;
  }

  try {
    await action();
  } finally {
    closeConfirmationDialog();
  }
};

const createDefaultVariantForm = () => ({
  id: null,
  localKey: nextVariantKey(),
  name: '',
  sku: '',
  size: '',
  color: '',
  presentation: '',
  price: 0,
  stockQuantity: 0,
  lowStockThreshold: 0,
  active: true,
  _destroy: false,
});

const createDefaultProductForm = () => ({
  name: '',
  sku: '',
  category: '',
  sourceType: 'product',
  source: 'manual',
  price: 0,
  currency: 'USD',
  active: true,
  description: '',
  trackStock: false,
  stockQuantity: 0,
  lowStockThreshold: 0,
  allowBackorder: false,
  galleryImageUrls: [''],
  automationRulesDefaultActions: [],
  variants: [],
});

const createDefaultActionForm = () => ({
  id: null,
  localKey: nextActionKey(),
  name: '',
  actionType: 'manual',
  triggerStatus: 'manual',
  templateKey: '',
  templatePayload: {},
  content: '',
  status: 'pending',
  completeOrder: false,
});

const createDefaultConfiguredActionForm = () => ({
  localKey: nextConfigActionKey(),
  name: '',
  actionType: 'manual',
  triggerStatus: 'manual',
  templateKey: '',
  templatePayload: {},
  content: '',
  completeOrder: false,
});

const createDefaultStuckRuleForm = () => ({
  localKey: nextStuckRuleKey(),
  status: 'pending',
  hours: 24,
  message: '',
  notifyResponsible: true,
  createConversationNote: true,
});

const createDefaultOrderForm = () => ({
  reference: '',
  productId: '',
  productVariantId: '',
  contactId: '',
  conversationId: '',
  contactName: '',
  contactPhone: '',
  contactEmail: '',
  address: '',
  location: '',
  trackingNumber: '',
  paymentMethod: '',
  paymentStatus: 'pending',
  paymentProofUrl: '',
  discountAmount: 0,
  shippingAmount: 0,
  paidAmount: 0,
  responsibleAgentId: '',
  fulfillmentNotes: '',
  quantity: 1,
  status: 'pending',
  source: 'manual',
  notes: '',
  actions: [createDefaultActionForm()],
});

const createDefaultModuleConfigForm = () => ({
  paymentMethods: [''],
  defaultActions: [],
  webhookUrls: [''],
  webhookEvents: [],
  stuckOrderRules: [],
});

const productForm = reactive(createDefaultProductForm());
const orderForm = reactive(createDefaultOrderForm());
const moduleConfigForm = reactive(createDefaultModuleConfigForm());

const visibleProductVariants = computed(() =>
  productForm.variants.filter(variant => !variant._destroy)
);

const isLoading = computed(
  () => uiFlags.value.isFetchingProducts || uiFlags.value.isFetchingProductOrders
);
const isProductSubmitting = computed(
  () => uiFlags.value.isCreatingProduct || uiFlags.value.isUpdatingProduct
);
const isOrderSubmitting = computed(
  () =>
    uiFlags.value.isCreatingProductOrder || uiFlags.value.isUpdatingProductOrder
);

const productTypeOptions = computed(() => [
  {
    value: 'product',
    label: t('PRODUCTS_ORDERS.PRODUCTS.TYPE_PRODUCT'),
  },
  {
    value: 'catalog',
    label: t('PRODUCTS_ORDERS.PRODUCTS.TYPE_CATALOG'),
  },
]);

const orderStatusOptions = computed(() => {
  const statuses = [...orderFlowStatuses.value, 'canceled'];
  const currentStatus = orderForm.status;

  if (currentStatus && !statuses.includes(currentStatus)) {
    statuses.unshift(currentStatus);
  }

  return statuses.map(status => ({
    value: status,
    label: t(`PRODUCTS_ORDERS.STATUS.${status.toUpperCase()}`),
  }));
});

const paymentStatusOptions = computed(() =>
  ['pending', 'partially_paid', 'paid', 'refunded', 'failed'].map(status => ({
    value: status,
    label: t(`PRODUCTS_ORDERS.PAYMENTS.STATUS.${status.toUpperCase()}`),
  }))
);

const paymentMethodOptions = computed(() => {
  const configuredMethods = Array.isArray(productsOrdersConfig.value.paymentMethods)
    ? productsOrdersConfig.value.paymentMethods
    : [];
  const methods = Array.from(
    new Set(
      [...configuredMethods, orderForm.paymentMethod]
        .map(method => method?.toString().trim())
        .filter(Boolean)
    )
  );

  return methods.map(method => ({
    value: method,
    label: method,
  }));
});

const orderFilterStatusOptions = computed(() => [
  {
    value: 'all',
    label: t('PRODUCTS_ORDERS.FILTERS.ALL_STATUSES'),
  },
  ...orderStatusOptions.value,
]);

const orderFilterProductOptions = computed(() => [
  {
    value: 'all',
    label: t('PRODUCTS_ORDERS.FILTERS.ALL_PRODUCTS'),
  },
  ...products.value.map(product => ({
    value: String(product.id),
    label: product.name,
  })),
]);

const agentOptions = computed(() =>
  agents.value.map(agent => ({
    value: String(agent.id),
    label: agent.name || agent.email,
  }))
);

const actionTypeOptions = computed(() => [
  {
    value: 'manual',
    label: t('PRODUCTS_ORDERS.ACTIONS.TYPE_MANUAL'),
  },
  {
    value: 'message',
    label: t('PRODUCTS_ORDERS.ACTIONS.TYPE_MESSAGE'),
  },
  {
    value: 'template',
    label: t('PRODUCTS_ORDERS.ACTIONS.TYPE_TEMPLATE'),
  },
]);

const actionTriggerOptions = computed(() => [
  {
    value: 'manual',
    label: t('PRODUCTS_ORDERS.ACTIONS.TRIGGER_MANUAL'),
  },
  ...Array.from(
    new Set([...STANDARD_ORDER_FLOW, ...ADVANCED_ORDER_FLOW])
  )
    .filter(status => status !== 'pending')
    .map(status => ({
      value: status,
      label: t('PRODUCTS_ORDERS.ACTIONS.TRIGGER_ON_STATUS', {
        status: statusLabel(status),
      }),
    })),
]);

const automationReminderStatusOptions = computed(() =>
  Array.from(new Set([...STANDARD_ORDER_FLOW, ...ADVANCED_ORDER_FLOW]))
    .filter(status => !['completed', 'delivered', 'canceled'].includes(status))
    .map(status => ({
      value: status,
      label: statusLabel(status),
    }))
);

const automationWebhookEventOptions = computed(() =>
  AUTOMATION_WEBHOOK_EVENTS.map(eventName => ({
    value: eventName,
    label: t(`PRODUCTS_ORDERS.AUTOMATIONS.WEBHOOK_EVENTS.${eventName.toUpperCase()}`),
  }))
);

const buildTemplateOptionValue = template =>
  `${template.__inboxId || 0}::${template.name}::${template.language || 'en'}`;

const availableWhatsAppTemplates = computed(() =>
  inboxes.value.flatMap(inbox =>
    store.getters['inboxes/getFilteredWhatsAppTemplates'](inbox.id).map(
      template => ({
        ...template,
        __inboxId: inbox.id,
        __inboxName: inbox.name,
      })
    )
  )
);

const linkedConversationInboxId = computed(
  () => linkedConversation.value?.inbox_id || linkedConversation.value?.inboxId || null
);

const linkedConversationInbox = computed(() =>
  inboxes.value.find(inbox => Number(inbox.id) === Number(linkedConversationInboxId.value))
);

const visibleWhatsAppTemplates = computed(() => {
  if (!linkedConversationInboxId.value) {
    return availableWhatsAppTemplates.value;
  }

  return availableWhatsAppTemplates.value.filter(
    template => Number(template.__inboxId) === Number(linkedConversationInboxId.value)
  );
});

const buildTemplateOptions = templates =>
  templates.map(template => ({
    value: buildTemplateOptionValue(template),
    label: `${template.name} (${template.language || 'en'}) - ${
      template.__inboxName
    }`,
  }));

const normalizeGalleryImageUrls = galleryImageUrls => {
  const sanitizedUrls = Array.isArray(galleryImageUrls)
    ? galleryImageUrls
        .map(url => url?.toString().trim())
        .filter(Boolean)
    : [];

  return sanitizedUrls.length ? sanitizedUrls : [''];
};

const productGalleryImages = product =>
  (product?.galleryImageUrls || [])
    .map(url => url?.toString().trim())
    .filter(Boolean);

const productPrimaryImage = product => productGalleryImages(product)[0] || '';

const summaryCards = computed(() => {
  const total = productOrders.value.length;
  const terminalStatus = orderFlowStatuses.value.at(-1);
  const pending = productOrders.value.filter(
    order => order.status === 'pending'
  ).length;
  const inProgressStatuses = orderFlowStatuses.value.slice(1, -1);
  const inProgress = productOrders.value.filter(order =>
    inProgressStatuses.includes(normalizeStatusForActiveFlow(order.status))
  ).length;
  const completed = productOrders.value.filter(
    order => normalizeStatusForActiveFlow(order.status) === terminalStatus
  ).length;

  return [
    { key: 'total', label: t('PRODUCTS_ORDERS.SUMMARY.TOTAL'), value: total },
    {
      key: 'pending',
      label: t('PRODUCTS_ORDERS.SUMMARY.PENDING'),
      value: pending,
    },
    {
      key: 'inProgress',
      label: t('PRODUCTS_ORDERS.SUMMARY.IN_PROGRESS'),
      value: inProgress,
    },
    {
      key: 'completed',
      label: t(
        productsOrdersFeatures.value.advancedStatuses
          ? 'PRODUCTS_ORDERS.SUMMARY.DELIVERED'
          : 'PRODUCTS_ORDERS.SUMMARY.COMPLETED'
      ),
      value: completed,
    },
  ];
});

const filteredProductOrders = computed(() => {
  const normalizedSearch = orderFilters.search.trim().toLowerCase();

  return productOrders.value.filter(order => {
    const matchesSearch =
      !normalizedSearch ||
      [
        order.reference,
        order.contactName,
        order.contactPhone,
        order.contactEmail,
        order.product?.name,
        order.productVariant?.name,
      ]
        .filter(Boolean)
        .some(value => String(value).toLowerCase().includes(normalizedSearch));

    const matchesStatus =
      orderFilters.status === 'all' || order.status === orderFilters.status;

    const matchesProduct =
      orderFilters.productId === 'all' ||
      Number(order.productId) === Number(orderFilters.productId);

    const createdDate = order.createdAt
      ? format(parseISO(order.createdAt), 'yyyy-MM-dd')
      : '';
    const matchesDateFrom =
      !orderFilters.dateFrom ||
      (createdDate && createdDate >= orderFilters.dateFrom);
    const matchesDateTo =
      !orderFilters.dateTo || (createdDate && createdDate <= orderFilters.dateTo);

    return (
      matchesSearch &&
      matchesStatus &&
      matchesProduct &&
      matchesDateFrom &&
      matchesDateTo
    );
  });
});

const hasActiveOrderFilters = computed(
  () =>
    !!orderFilters.search ||
    orderFilters.status !== 'all' ||
    orderFilters.productId !== 'all' ||
    !!orderFilters.dateFrom ||
    !!orderFilters.dateTo
);

const filteredOrderAnalytics = computed(() => {
  const scopedOrders = filteredProductOrders.value;
  const total = scopedOrders.length;
  const completedCount = scopedOrders.filter(order =>
    ['completed', 'delivered'].includes(order.status)
  ).length;
  const canceledCount = scopedOrders.filter(
    order => order.status === 'canceled'
  ).length;
  const completedOrders = scopedOrders.filter(
    order => order.completedAt && order.createdAt
  );
  const averageCompletionHours = completedOrders.length
    ? completedOrders.reduce((sum, order) => {
        const durationMs =
          new Date(order.completedAt).getTime() -
          new Date(order.createdAt).getTime();
        return sum + durationMs / 3_600_000;
      }, 0) / completedOrders.length
    : 0;
  const productFrequency = scopedOrders.reduce((acc, order) => {
    const productName = productNameForOrder(order);
    acc[productName] = (acc[productName] || 0) + 1;
    return acc;
  }, {});
  const [topProductName, topProductCount] =
    Object.entries(productFrequency).sort(([, a], [, b]) => b - a)[0] || [];

  return [
    {
      key: 'completionRate',
      label: t('PRODUCTS_ORDERS.ANALYTICS.COMPLETION_RATE'),
      value: total ? `${Math.round((completedCount / total) * 100)}%` : '0%',
      helper: t('PRODUCTS_ORDERS.ANALYTICS.COMPLETION_RATE_HELPER', {
        completed: completedCount,
        total,
      }),
    },
    {
      key: 'cancellationRate',
      label: t('PRODUCTS_ORDERS.ANALYTICS.CANCELLATION_RATE'),
      value: total ? `${Math.round((canceledCount / total) * 100)}%` : '0%',
      helper: t('PRODUCTS_ORDERS.ANALYTICS.CANCELLATION_RATE_HELPER', {
        canceled: canceledCount,
        total,
      }),
    },
    {
      key: 'avgCompletionTime',
      label: t('PRODUCTS_ORDERS.ANALYTICS.AVG_COMPLETION_TIME'),
      value: `${averageCompletionHours.toFixed(1)}h`,
      helper: t('PRODUCTS_ORDERS.ANALYTICS.AVG_COMPLETION_TIME_HELPER'),
    },
    {
      key: 'topProduct',
      label: t('PRODUCTS_ORDERS.ANALYTICS.TOP_PRODUCT'),
      value: topProductName || t('PRODUCTS_ORDERS.LABELS.NOT_AVAILABLE'),
      helper: topProductName
        ? t('PRODUCTS_ORDERS.ANALYTICS.TOP_PRODUCT_HELPER', {
            count: topProductCount,
          })
        : t('PRODUCTS_ORDERS.ANALYTICS.TOP_PRODUCT_EMPTY'),
    },
  ];
});

const kanbanColumns = computed(() =>
  [...orderFlowStatuses.value, 'canceled'].map(status => ({
    status,
    label: statusLabel(status),
    orders: filteredProductOrders.value.filter(order => {
      if (status === 'canceled') {
        return order.status === 'canceled';
      }

      return normalizeStatusForActiveFlow(order.status) === status;
    }),
  }))
);

const mapConfiguredActionToOrderAction = action => ({
  ...createDefaultActionForm(),
  name: action?.name || '',
  actionType: action?.actionType || action?.action_type || 'manual',
  triggerStatus: action?.triggerStatus || action?.trigger_status || 'manual',
  templateKey:
    action?.templateKey || templateSelectionForPayload(action?.templatePayload || action?.template_payload),
  templatePayload: action?.templatePayload || action?.template_payload || {},
  content: action?.content || '',
  completeOrder: !!(action?.completeOrder ?? action?.complete_order),
});

const mapConfiguredActionToForm = action => ({
  ...createDefaultConfiguredActionForm(),
  name: action?.name || '',
  actionType: action?.actionType || action?.action_type || 'manual',
  triggerStatus: action?.triggerStatus || action?.trigger_status || 'manual',
  templateKey:
    action?.templateKey ||
    templateSelectionForPayload(
      action?.templatePayload || action?.template_payload
    ),
  templatePayload: action?.templatePayload || action?.template_payload || {},
  content: action?.content || '',
  completeOrder: !!(action?.completeOrder ?? action?.complete_order),
});

const isActionPayloadComplete = action => {
  const actionType = action?.actionType || action?.action_type || 'manual';
  const hasName = !!action?.name?.trim?.();

  if (!hasName) {
    return false;
  }

  if (actionType === 'message') {
    return !!action?.content?.trim?.();
  }

  if (actionType === 'template') {
    return !!(
      action?.templateKey ||
      action?.templatePayload?.name ||
      action?.template_payload?.name
    );
  }

  return true;
};

const configuredActionsPayloadFor = actions =>
  actions
    .filter(isActionPayloadComplete)
    .map(buildConfiguredActionPayload);

const productAutomationActionsFor = product => {
  const configuredActions = Array.isArray(product?.automationRules?.defaultActions)
    ? product.automationRules.defaultActions
    : [];

  return configuredActions.length
    ? configuredActions.map(mapConfiguredActionToOrderAction)
    : null;
};

const buildInitialOrderActions = productId => {
  const selectedProductConfig =
    products.value.find(product => Number(product.id) === Number(productId)) ||
    null;
  const productActions = productAutomationActionsFor(selectedProductConfig);
  if (productActions?.length) {
    return productActions;
  }

  const configuredActions = Array.isArray(productsOrdersConfig.value.defaultActions)
    ? productsOrdersConfig.value.defaultActions
    : [];

  if (!configuredActions.length) {
    return [createDefaultActionForm()];
  }

  return configuredActions.map(mapConfiguredActionToOrderAction);
};

const resetProductForm = () => {
  Object.assign(productForm, createDefaultProductForm());
  productForm.galleryImageUrls = [''];
  editingProductId.value = null;
};

const resetOrderForm = () => {
  Object.assign(orderForm, {
    ...createDefaultOrderForm(),
    actions: buildInitialOrderActions(''),
  });
  orderForm.paymentMethod =
    paymentMethodOptions.value[0]?.value || orderForm.paymentMethod;
  deletedActionIds.value = [];
  editingOrderId.value = null;
  templateParserRefs.value = {};
  linkedConversation.value = null;
  conversationContextError.value = '';
  isFetchingConversationContext.value = false;
  lastResolvedConversationId.value = null;
  conversationContextRequestToken.value = 0;
  selectedOrderContactId.value = '';
};

const resetExecutionPreview = () => {
  pendingExecution.value = {
    order: null,
    action: null,
  };
};

const resetModuleConfigForm = () => {
  const paymentMethods = Array.isArray(productsOrdersConfig.value.paymentMethods)
    ? productsOrdersConfig.value.paymentMethods
    : [];
  const defaultActions = Array.isArray(productsOrdersConfig.value.defaultActions)
    ? productsOrdersConfig.value.defaultActions
    : [];
  const webhookUrls = Array.isArray(productsOrdersConfig.value.webhookUrls)
    ? productsOrdersConfig.value.webhookUrls
    : [];
  const webhookEvents = Array.isArray(productsOrdersConfig.value.webhookEvents)
    ? productsOrdersConfig.value.webhookEvents
    : [];
  const stuckOrderRules = Array.isArray(productsOrdersConfig.value.stuckOrderRules)
    ? productsOrdersConfig.value.stuckOrderRules
    : [];

  Object.assign(moduleConfigForm, {
    paymentMethods: paymentMethods.length ? [...paymentMethods] : [''],
    defaultActions: defaultActions.length
      ? defaultActions.map(mapConfiguredActionToForm)
      : [],
    webhookUrls: webhookUrls.length ? [...webhookUrls] : [''],
    webhookEvents: [...webhookEvents],
    stuckOrderRules: stuckOrderRules.length
      ? stuckOrderRules.map(rule => ({
          ...createDefaultStuckRuleForm(),
          status: rule?.status || 'pending',
          hours: Number(rule?.hours || 24),
          message: rule?.message || '',
          notifyResponsible: !!(
            rule?.notifyResponsible ?? rule?.notify_responsible
          ),
          createConversationNote: !!(
            rule?.createConversationNote ?? rule?.create_conversation_note
          ),
        }))
      : [],
  });
};

const formatDateTime = value => {
  if (!value) return t('PRODUCTS_ORDERS.LABELS.NOT_AVAILABLE');

  return format(parseISO(value), 'MMM d, yyyy HH:mm');
};

const formatPrice = product => {
  const amount = Number(product?.price || 0);
  const currency = product?.currency || 'USD';

  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (error) {
    return `${currency} ${amount.toFixed(2)}`;
  }
};

const formatCurrencyAmount = (amount, currency = 'USD') => {
  const normalizedAmount = Number(amount || 0);

  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(normalizedAmount);
  } catch (error) {
    return `${currency} ${normalizedAmount.toFixed(2)}`;
  }
};

const productNameForOrder = order => {
  if (order.productVariant?.name && order.product?.name) {
    return `${order.product.name} · ${order.productVariant.name}`;
  }

  if (order.productVariant?.name) {
    return order.productVariant.name;
  }

  return (
    order.product?.name || t('PRODUCTS_ORDERS.LABELS.PRODUCT_NOT_SELECTED')
  );
};

const inventoryStatusClass = status => {
  const classes = {
    not_tracked:
      'border-n-slate-6/40 bg-n-alpha-2 text-n-slate-11 dark:border-n-slate-7/40 dark:bg-n-alpha-2 dark:text-n-slate-11',
    in_stock:
      'border-n-teal-6/40 bg-n-teal-3 text-n-teal-11 dark:border-n-teal-9/40 dark:bg-n-teal-3/10 dark:text-n-teal-11',
    low_stock:
      'border-n-amber-6/40 bg-n-amber-3 text-n-amber-11 dark:border-n-amber-9/40 dark:bg-n-amber-3/10 dark:text-n-amber-11',
    out_of_stock:
      'border-n-ruby-6/40 bg-n-ruby-3 text-n-ruby-11 dark:border-n-ruby-9/40 dark:bg-n-ruby-3/10 dark:text-n-ruby-11',
  };

  return classes[status] || classes.not_tracked;
};

const inventoryStatusLabel = status =>
  t(`PRODUCTS_ORDERS.INVENTORY.STATUS.${String(status || 'not_tracked').toUpperCase()}`);

const hasProductVariants = product =>
  (product?.productVariants || []).some(variant => variant.active);

const productInventorySummary = product => {
  if (!product?.trackStock) {
    return t('PRODUCTS_ORDERS.INVENTORY.NOT_TRACKED');
  }

  if (hasProductVariants(product)) {
    return t('PRODUCTS_ORDERS.INVENTORY.VARIANTS_AVAILABLE_STOCK', {
      variants: product.productVariants.filter(variant => variant.active).length,
      count: product.availableStock ?? 0,
    });
  }

  return t('PRODUCTS_ORDERS.INVENTORY.AVAILABLE_STOCK', {
    count: product.availableStock ?? product.stockQuantity ?? 0,
  });
};

const variantDisplayName = variant =>
  [
    variant.name,
    variant.size,
    variant.color,
    variant.presentation,
  ]
    .filter(Boolean)
    .join(' · ');

const statusBadgeClass = status => {
  const classes = {
    pending:
      'border-n-amber-6/40 bg-n-amber-3 text-n-amber-11 dark:border-n-amber-9/40 dark:bg-n-amber-3/10 dark:text-n-amber-11',
    confirmed:
      'border-n-blue-6/40 bg-n-blue-3 text-n-blue-11 dark:border-n-blue-9/40 dark:bg-n-blue-3/10 dark:text-n-blue-11',
    preparing:
      'border-n-indigo-6/40 bg-n-indigo-3 text-n-indigo-11 dark:border-n-indigo-9/40 dark:bg-n-indigo-3/10 dark:text-n-indigo-11',
    shipped:
      'border-n-cyan-6/40 bg-n-cyan-3 text-n-cyan-11 dark:border-n-cyan-9/40 dark:bg-n-cyan-3/10 dark:text-n-cyan-11',
    delivered:
      'border-n-teal-6/40 bg-n-teal-3 text-n-teal-11 dark:border-n-teal-9/40 dark:bg-n-teal-3/10 dark:text-n-teal-11',
    in_progress:
      'border-n-blue-6/40 bg-n-brand/10 text-n-blue-11 dark:border-n-blue-9/40 dark:bg-n-brand/10 dark:text-n-blue-11',
    completed:
      'border-n-teal-6/40 bg-n-teal-3 text-n-teal-11 dark:border-n-teal-9/40 dark:bg-n-teal-3/10 dark:text-n-teal-11',
    canceled:
      'border-n-ruby-6/40 bg-n-ruby-3 text-n-ruby-11 dark:border-n-ruby-9/40 dark:bg-n-ruby-3/10 dark:text-n-ruby-11',
    failed:
      'border-n-ruby-6/40 bg-n-ruby-3 text-n-ruby-11 dark:border-n-ruby-9/40 dark:bg-n-ruby-3/10 dark:text-n-ruby-11',
    skipped:
      'border-n-slate-6/40 bg-n-alpha-2 text-n-slate-11 dark:border-n-slate-7/40 dark:bg-n-alpha-2 dark:text-n-slate-11',
  };

  return classes[status] || classes.pending;
};

const paymentStatusBadgeClass = status => {
  const classes = {
    pending:
      'border-n-amber-6/40 bg-n-amber-3 text-n-amber-11 dark:border-n-amber-9/40 dark:bg-n-amber-3/10 dark:text-n-amber-11',
    partially_paid:
      'border-n-blue-6/40 bg-n-blue-3 text-n-blue-11 dark:border-n-blue-9/40 dark:bg-n-blue-3/10 dark:text-n-blue-11',
    paid:
      'border-n-teal-6/40 bg-n-teal-3 text-n-teal-11 dark:border-n-teal-9/40 dark:bg-n-teal-3/10 dark:text-n-teal-11',
    refunded:
      'border-n-indigo-6/40 bg-n-indigo-3 text-n-indigo-11 dark:border-n-indigo-9/40 dark:bg-n-indigo-3/10 dark:text-n-indigo-11',
    failed:
      'border-n-ruby-6/40 bg-n-ruby-3 text-n-ruby-11 dark:border-n-ruby-9/40 dark:bg-n-ruby-3/10 dark:text-n-ruby-11',
  };

  return classes[status] || classes.pending;
};

const statusLabel = status =>
  t(`PRODUCTS_ORDERS.STATUS.${status.toUpperCase()}`);

const paymentStatusLabel = status =>
  t(`PRODUCTS_ORDERS.PAYMENTS.STATUS.${String(status || 'pending').toUpperCase()}`);

const actionTypeLabel = actionType =>
  t(`PRODUCTS_ORDERS.ACTIONS.TYPE_${actionType.toUpperCase()}`);

const normalizeStatusForActiveFlow = status => {
  if (productsOrdersFeatures.value.advancedStatuses) {
    if (status === 'in_progress') return 'preparing';
    if (status === 'completed') return 'delivered';
    return status;
  }

  if (['confirmed', 'preparing', 'shipped'].includes(status)) {
    return 'in_progress';
  }
  if (status === 'delivered') {
    return 'completed';
  }

  return status;
};

const nextStatusForOrder = order => {
  const normalizedStatus = normalizeStatusForActiveFlow(order.status);
  const currentIndex = orderFlowStatuses.value.indexOf(normalizedStatus);
  if (currentIndex === -1) {
    return null;
  }

  return orderFlowStatuses.value[currentIndex + 1] || null;
};

const canCancelOrder = order => {
  const normalizedStatus = normalizeStatusForActiveFlow(order.status);
  const terminalStatus = orderFlowStatuses.value.at(-1);

  return !['canceled', terminalStatus].includes(normalizedStatus);
};

const isTerminalOrderStatus = order => {
  const normalizedStatus = normalizeStatusForActiveFlow(order.status);
  return ['canceled', orderFlowStatuses.value.at(-1)].includes(normalizedStatus);
};

const timelineDescription = event => {
  const actor = event.actor?.name || t('PRODUCTS_ORDERS.LABELS.SYSTEM');

  if (event.eventType === 'created') {
    return t('PRODUCTS_ORDERS.TIMELINE.CREATED', { actor });
  }

  if (event.eventType === 'status_changed') {
    return t('PRODUCTS_ORDERS.TIMELINE.STATUS_CHANGED', {
      actor,
      from: statusLabel(event.statusFrom),
      to: statusLabel(event.statusTo),
    });
  }

  if (event.eventType === 'payment_status_changed') {
    return t('PRODUCTS_ORDERS.TIMELINE.PAYMENT_STATUS_CHANGED', {
      actor,
      from: paymentStatusLabel(event.statusFrom),
      to: paymentStatusLabel(event.statusTo),
    });
  }

  if (event.eventType === 'stuck_alerted') {
    return t('PRODUCTS_ORDERS.TIMELINE.STUCK_ALERTED', {
      status: statusLabel(event.metadata?.status || 'pending'),
      hours: event.metadata?.thresholdHours || event.metadata?.threshold_hours || 0,
    });
  }

  if (event.eventType === 'webhook_dispatched') {
    return t('PRODUCTS_ORDERS.TIMELINE.WEBHOOK_DISPATCHED', {
      event: event.metadata?.eventName || event.metadata?.event_name || t('PRODUCTS_ORDERS.LABELS.NOT_AVAILABLE'),
      count: event.metadata?.webhookCount || event.metadata?.webhook_count || 0,
    });
  }

  return t('PRODUCTS_ORDERS.TIMELINE.ACTION_EXECUTED', {
    actor,
    action: event.metadata?.actionName || t('PRODUCTS_ORDERS.LABELS.NOT_AVAILABLE'),
  });
};

const normalizeConversationId = value => {
  const numericValue = Number(value);

  if (!numericValue || Number.isNaN(numericValue) || numericValue < 1) {
    return null;
  }

  return numericValue;
};

const syncConversationContext = async conversationIdValue => {
  const normalizedConversationId = normalizeConversationId(conversationIdValue);

  if (!normalizedConversationId) {
    conversationContextRequestToken.value += 1;
    linkedConversation.value = null;
    conversationContextError.value = '';
    lastResolvedConversationId.value = null;
    isFetchingConversationContext.value = false;
    return;
  }

  if (lastResolvedConversationId.value === normalizedConversationId) {
    return;
  }

  conversationContextRequestToken.value += 1;
  const requestToken = conversationContextRequestToken.value;
  isFetchingConversationContext.value = true;
  conversationContextError.value = '';

  try {
    const response = await ConversationAPI.show(normalizedConversationId);
    if (requestToken !== conversationContextRequestToken.value) {
      return;
    }
    linkedConversation.value = response.data;
    lastResolvedConversationId.value = normalizedConversationId;
  } catch (error) {
    if (requestToken !== conversationContextRequestToken.value) {
      return;
    }
    linkedConversation.value = null;
    conversationContextError.value = t(
      'PRODUCTS_ORDERS.LABELS.CONVERSATION_CONTEXT_UNAVAILABLE'
    );
    lastResolvedConversationId.value = null;
  } finally {
    if (requestToken === conversationContextRequestToken.value) {
      isFetchingConversationContext.value = false;
    }
  }
};

const buildAddressFromSender = sender => {
  return (
    sender?.additional_attributes?.address ||
    sender?.additional_attributes?.address_line_1 ||
    ''
  );
};

const buildConversationPrefillKey = () => {
  const { openOrder, conversationId } = route.query;

  if (openOrder !== 'conversation' || !conversationId) {
    return '';
  }

  return `${openOrder}:${conversationId}`;
};

const clearConversationPrefillQuery = () => {
  const nextQuery = { ...route.query };
  delete nextQuery.openOrder;
  delete nextQuery.conversationId;

  router.replace({ query: nextQuery });
};

const applyConversationPrefill = async () => {
  const prefillKey = buildConversationPrefillKey();
  if (!prefillKey || handledConversationPrefillKey.value === prefillKey) {
    return;
  }

  const normalizedConversationId = normalizeConversationId(route.query.conversationId);
  handledConversationPrefillKey.value = prefillKey;

  resetOrderForm();

  if (normalizedConversationId) {
    await syncConversationContext(normalizedConversationId);
  }

  const sender = linkedConversation.value?.meta?.sender || {};

  suppressConversationContextWatch.value = true;
  Object.assign(orderForm, {
    conversationId: normalizedConversationId || '',
    contactId: sender.id || '',
    contactName:
      sender.name || sender.email || sender.phone_number || orderForm.contactName,
    contactPhone: sender.phone_number || '',
    contactEmail: sender.email || '',
    address: buildAddressFromSender(sender),
    source: 'conversation',
  });
  if (sender.id) {
    appendOrderContactOption({
      value: String(sender.id),
      label:
        sender.name || sender.email || sender.phone_number || String(sender.id),
      name: sender.name || sender.email || sender.phone_number || '',
      email: sender.email || '',
      phoneNumber: sender.phone_number || '',
      address: buildAddressFromSender(sender),
      location: sender.location || '',
    });
    selectedOrderContactId.value = String(sender.id);
  }
  await nextTick();
  suppressConversationContextWatch.value = false;

  orderDialogRef.value?.open?.();
  clearConversationPrefillQuery();
};

const selectedProduct = computed(() =>
  products.value.find(product => Number(product.id) === Number(orderForm.productId))
);

const selectedProductVariants = computed(() =>
  (selectedProduct.value?.productVariants || []).filter(variant => variant.active)
);

const selectedProductVariant = computed(() =>
  selectedProductVariants.value.find(
    variant => Number(variant.id) === Number(orderForm.productVariantId)
  )
);

const orderRequiresVariant = computed(() => selectedProductVariants.value.length > 0);
const selectedOrderInventoryItem = computed(
  () => selectedProductVariant.value || selectedProduct.value
);
const orderTracksStock = computed(() => !!selectedProduct.value?.trackStock);
const orderAllowsBackorder = computed(() => !!selectedProduct.value?.allowBackorder);
const orderCurrency = computed(
  () => selectedProductVariant.value?.currency || selectedProduct.value?.currency || 'USD'
 );
const selectedProductUnitPrice = computed(() =>
  Number(selectedProductVariant.value?.price ?? selectedProduct.value?.price ?? 0)
);
const orderAvailableStock = computed(() =>
  Number(
    selectedProductVariant.value?.stockQuantity ??
      selectedProduct.value?.availableStock ??
      selectedProduct.value?.stockQuantity ??
      0
  )
);
const orderStockBlocked = computed(
  () =>
    !!selectedProduct.value &&
    orderTracksStock.value &&
    !orderAllowsBackorder.value &&
    orderAvailableStock.value < Number(orderForm.quantity || 1)
);
const canSubmitOrder = computed(
  () =>
    !!orderForm.contactName.trim() &&
    (!selectedProduct.value ||
      !orderRequiresVariant.value ||
      !!orderForm.productVariantId) &&
    !orderStockBlocked.value
);
const orderSubtotalPreview = computed(
  () => selectedProductUnitPrice.value * Number(orderForm.quantity || 1)
);
const orderTotalPreview = computed(() =>
  Math.max(
    0,
    orderSubtotalPreview.value -
      Number(orderForm.discountAmount || 0) +
      Number(orderForm.shippingAmount || 0)
  )
);
const orderOutstandingPreview = computed(() =>
  Math.max(0, orderTotalPreview.value - Number(orderForm.paidAmount || 0))
);
const orderPaymentStatusPreview = computed(() => {
  if (['failed', 'refunded'].includes(orderForm.paymentStatus)) {
    return orderForm.paymentStatus;
  }

  if (Number(orderForm.paidAmount || 0) <= 0) {
    return 'pending';
  }

  if (orderOutstandingPreview.value <= 0) {
    return 'paid';
  }

  return 'partially_paid';
});

const selectedTemplateForAction = action => {
  if (!action.templateKey) {
    return null;
  }

  return (
    availableWhatsAppTemplates.value.find(
      template => buildTemplateOptionValue(template) === action.templateKey
    ) || null
  );
};

const productDefaultActionsCount = product =>
  Array.isArray(product?.automationRules?.defaultActions)
    ? product.automationRules.defaultActions.length
    : 0;

const templateOptionsForAction = action => {
  const selectedTemplate = selectedTemplateForAction(action);

  if (!selectedTemplate) {
    return buildTemplateOptions(visibleWhatsAppTemplates.value);
  }

  const selectedValue = buildTemplateOptionValue(selectedTemplate);
  const templates = visibleWhatsAppTemplates.value.some(
    template => buildTemplateOptionValue(template) === selectedValue
  )
    ? visibleWhatsAppTemplates.value
    : [...visibleWhatsAppTemplates.value, selectedTemplate];

  return buildTemplateOptions(templates);
};

const templateSelectionForPayload = templatePayload => {
  if (!templatePayload?.name) {
    return '';
  }

  const matchingTemplate = availableWhatsAppTemplates.value.find(template => {
    const sameName = template.name === templatePayload.name;
    const sameLanguage =
      (template.language || 'en') === (templatePayload.language || 'en');
    const sameNamespace =
      !templatePayload.namespace ||
      template.namespace === templatePayload.namespace;

    return sameName && sameLanguage && sameNamespace;
  });

  return matchingTemplate ? buildTemplateOptionValue(matchingTemplate) : '';
};

const appendOrderContactOption = contact => {
  if (!contact?.value) {
    return;
  }

  orderContactOptions.value = [
    contact,
    ...orderContactOptions.value.filter(
      existing => String(existing.value) !== String(contact.value)
    ),
  ];
};

const normalizedOrderContactOption = contact => {
  const contactName =
    contact.name || contact.identifier || contact.email || contact.phoneNumber || '';
  const email = contact.email || '';
  const phoneNumber = contact.phoneNumber || contact.phone_number || '';
  const metadata = [email, phoneNumber].filter(Boolean).join(' · ');

  return {
    value: String(contact.id),
    label: metadata ? `${contactName} (${metadata})` : contactName,
    name: contactName,
    email,
    phoneNumber,
    location: contact.location || '',
    address:
      contact.additionalAttributes?.address ||
      contact.additional_attributes?.address ||
      contact.additionalAttributes?.addressLine1 ||
      contact.additional_attributes?.address_line_1 ||
      '',
  };
};

const loadOrderContactOptions = async searchQuery => {
  const preservedSelectedContact =
    selectedOrderContact.value ||
    (selectedOrderContactId.value
      ? {
          value: String(selectedOrderContactId.value),
          label: orderForm.contactEmail
            ? `${orderForm.contactName} (${orderForm.contactEmail})`
            : orderForm.contactPhone
              ? `${orderForm.contactName} (${orderForm.contactPhone})`
              : orderForm.contactName,
          name: orderForm.contactName,
          email: orderForm.contactEmail,
          phoneNumber: orderForm.contactPhone,
          location: orderForm.location,
          address: orderForm.address,
        }
      : null);
  orderContactsRequestController.value?.abort?.();
  const controller = new AbortController();
  orderContactsRequestController.value = controller;
  isSearchingOrderContacts.value = true;

  try {
    const {
      data: { payload = [] },
    } = await ContactAPI.search(searchQuery, 1, 'name', '', {
      signal: controller.signal,
    });

    orderContactOptions.value = camelcaseKeys(payload, { deep: true }).map(
      normalizedOrderContactOption
    );
    if (preservedSelectedContact?.value) {
      appendOrderContactOption(preservedSelectedContact);
    }
  } catch (error) {
    if (error.name !== 'CanceledError' && error.name !== 'AbortError') {
      orderContactOptions.value = [];
    }
  } finally {
    if (orderContactsRequestController.value === controller) {
      isSearchingOrderContacts.value = false;
    }
  }
};

const debouncedLoadOrderContacts = debounce(searchQuery => {
  loadOrderContactOptions(searchQuery);
}, 250);

const primeOrderContactSearch = async () => {
  if (orderContactOptions.value.length) {
    return;
  }

  await loadOrderContactOptions('');
};

const selectedOrderContact = computed(() =>
  orderContactOptions.value.find(
    contact => String(contact.value) === String(selectedOrderContactId.value)
  )
);

const openQuickOrderContactDialog = () => {
  orderQuickContactDialogRef.value?.dialogRef.open();
};

const createQuickOrderContact = async contact => {
  try {
    const createdContact = await store.dispatch('contacts/create', contact);
    orderQuickContactDialogRef.value?.onSuccess();
    const normalizedContact = normalizedOrderContactOption(createdContact);
    appendOrderContactOption(normalizedContact);
    selectedOrderContactId.value = normalizedContact.value;
    orderForm.contactId = normalizedContact.value;
    orderForm.contactName = normalizedContact.name;
    orderForm.contactPhone = normalizedContact.phoneNumber;
    orderForm.contactEmail = normalizedContact.email;
    orderForm.location = normalizedContact.location || orderForm.location;
    orderForm.address = normalizedContact.address || orderForm.address;
    useAlert(t('PRODUCTS_ORDERS.ALERTS.CONTACT_CREATED'));
  } catch (error) {
    useAlert(t('PRODUCTS_ORDERS.ALERTS.CONTACT_CREATE_ERROR'));
  }
};

const setTemplateParserRef = (key, element) => {
  if (element) {
    templateParserRefs.value[key] = element;
    return;
  }

  delete templateParserRefs.value[key];
};

const buildTemplateAutoFillValues = () => [
  orderForm.contactName.trim(),
  orderForm.reference.trim(),
  selectedProduct.value?.name || '',
  selectedProductVariant.value?.name || '',
  String(orderForm.quantity || ''),
  orderForm.contactPhone.trim(),
  orderForm.contactEmail.trim(),
  orderForm.address.trim(),
  orderForm.location.trim(),
  orderForm.notes.trim(),
  orderForm.source.trim(),
];

const buildTemplateProcessedParams = (template, existingProcessedParams = {}) => {
  const headerComponent = findComponentByType(template, COMPONENT_TYPES.HEADER);
  const hasMediaHeader = MEDIA_FORMATS.includes(headerComponent?.format);
  const baseParams = buildTemplateParameters(template, hasMediaHeader);
  const autoFillValues = buildTemplateAutoFillValues();

  if (baseParams.body) {
    Object.keys(baseParams.body).forEach((key, index) => {
      baseParams.body[key] =
        existingProcessedParams?.body?.[key] || autoFillValues[index] || '';
    });
  }

  if (baseParams.buttons) {
    baseParams.buttons = baseParams.buttons.map((button, index) => ({
      ...button,
      parameter:
        existingProcessedParams?.buttons?.[index]?.parameter ||
        orderForm.reference.trim(),
    }));
  }

  if (baseParams.header) {
    baseParams.header = {
      ...baseParams.header,
      media_url: existingProcessedParams?.header?.media_url || '',
      media_name: existingProcessedParams?.header?.media_name || '',
    };
  }

  return baseParams;
};

const syncTemplateParserState = async (
  action,
  processedParams = action.templatePayload?.processed_params
) => {
  const template = selectedTemplateForAction(action);
  if (!template) return;

  await nextTick();
  const parser = templateParserRefs.value[action.localKey];
  if (!parser) return;

  parser.processedParams = buildTemplateProcessedParams(
    template,
    processedParams
  );
};

const autofillTemplateVariables = action => {
  syncTemplateParserState(action, {});
};

const handleTemplateSelection = async (action, templateKey) => {
  action.templateKey = templateKey;
  action.templatePayload = {};
  action.content = '';
  await syncTemplateParserState(action, {});
};

const executeActionLabel = action => {
  if (action.actionType === 'template') {
    return (
      action.content ||
      action.templatePayload?.name ||
      t('PRODUCTS_ORDERS.LABELS.TEMPLATE_READY')
    );
  }

  return action.content || t('PRODUCTS_ORDERS.ORDERS.NO_ACTIONS');
};

const canExecuteAction = (order, action) => {
  if (action.status === 'completed') return false;
  if (!['message', 'template'].includes(action.actionType)) return true;

  return !!order.conversationId;
};

const openExecutionPreview = (order, action) => {
  if (!canExecuteAction(order, action)) {
    return;
  }

  pendingExecution.value = { order, action };
  executionPreviewDialogRef.value?.open?.();
};

const executionPreviewContent = computed(() => {
  const action = pendingExecution.value.action;
  if (!action) {
    return '';
  }

  return (
    action.content ||
    action.templatePayload?.name ||
    t('PRODUCTS_ORDERS.LABELS.NOT_AVAILABLE')
  );
});

const addActionRow = () => {
  orderForm.actions = [...orderForm.actions, createDefaultActionForm()];
};

const addVariantRow = () => {
  productForm.variants = [...productForm.variants, createDefaultVariantForm()];
};

const addGalleryImageRow = () => {
  productForm.galleryImageUrls = [...productForm.galleryImageUrls, ''];
};

const removeGalleryImageRow = index => {
  const nextImages = productForm.galleryImageUrls.filter(
    (_image, imageIndex) => imageIndex !== index
  );
  productForm.galleryImageUrls = nextImages.length ? nextImages : [''];
};

const addPaymentMethodRow = () => {
  moduleConfigForm.paymentMethods = [...moduleConfigForm.paymentMethods, ''];
};

const addWebhookUrlRow = () => {
  moduleConfigForm.webhookUrls = [...moduleConfigForm.webhookUrls, ''];
};

const removePaymentMethodRow = index => {
  const nextMethods = moduleConfigForm.paymentMethods.filter(
    (_method, methodIndex) => methodIndex !== index
  );
  moduleConfigForm.paymentMethods = nextMethods.length ? nextMethods : [''];
};

const removeWebhookUrlRow = index => {
  const nextUrls = moduleConfigForm.webhookUrls.filter(
    (_url, urlIndex) => urlIndex !== index
  );
  moduleConfigForm.webhookUrls = nextUrls.length ? nextUrls : [''];
};

const addConfiguredActionRow = () => {
  moduleConfigForm.defaultActions = [
    ...moduleConfigForm.defaultActions,
    createDefaultConfiguredActionForm(),
  ];
};

const addProductAutomationActionRow = () => {
  productForm.automationRulesDefaultActions = [
    ...productForm.automationRulesDefaultActions,
    createDefaultConfiguredActionForm(),
  ];
};

const removeProductAutomationActionRow = actionToRemove => {
  productForm.automationRulesDefaultActions =
    productForm.automationRulesDefaultActions.filter(
      action => action.localKey !== actionToRemove.localKey
    );
};

const addStuckOrderRuleRow = () => {
  moduleConfigForm.stuckOrderRules = [
    ...moduleConfigForm.stuckOrderRules,
    createDefaultStuckRuleForm(),
  ];
};

const removeStuckOrderRuleRow = ruleToRemove => {
  moduleConfigForm.stuckOrderRules = moduleConfigForm.stuckOrderRules.filter(
    rule => rule.localKey !== ruleToRemove.localKey
  );
};

const removeConfiguredActionRow = configAction => {
  moduleConfigForm.defaultActions = moduleConfigForm.defaultActions.filter(
    action => action.localKey !== configAction.localKey
  );
};

const removeVariantRow = variantToRemove => {
  if (variantToRemove.id) {
    variantToRemove._destroy = true;
    return;
  }

  productForm.variants = productForm.variants.filter(
    variant => variant.localKey !== variantToRemove.localKey
  );
};

const removeActionRow = actionToRemove => {
  if (actionToRemove.id) {
    deletedActionIds.value = [...deletedActionIds.value, actionToRemove.id];
  }

  orderForm.actions = orderForm.actions.filter(
    action => action.localKey !== actionToRemove.localKey
  );

  if (!orderForm.actions.length) {
    addActionRow();
  }
};

const openModuleSettingsDialog = () => {
  resetModuleConfigForm();
  settingsDialogRef.value?.open?.();
};

const openProductDialog = product => {
  resetProductForm();

  if (product) {
    editingProductId.value = product.id;
    Object.assign(productForm, {
      name: product.name,
      sku: product.sku || '',
      category: product.category || '',
      sourceType: product.sourceType,
      source: product.source,
      price: Number(product.price || 0),
      currency: product.currency,
      active: product.active,
      description: product.description || '',
      trackStock: !!product.trackStock,
      stockQuantity: Number(product.stockQuantity || 0),
      lowStockThreshold: Number(product.lowStockThreshold || 0),
      allowBackorder: !!product.allowBackorder,
      galleryImageUrls: normalizeGalleryImageUrls(product.galleryImageUrls),
      automationRulesDefaultActions: (
        product.automationRules?.defaultActions || []
      ).map(mapConfiguredActionToForm),
      variants: (product.productVariants || []).map(variant => ({
        id: variant.id,
        localKey: nextVariantKey(),
        name: variant.name,
        sku: variant.sku || '',
        size: variant.size || '',
        color: variant.color || '',
        presentation: variant.presentation || '',
        price: Number(variant.price || 0),
        stockQuantity: Number(variant.stockQuantity || 0),
        lowStockThreshold: Number(variant.lowStockThreshold || 0),
        active: !!variant.active,
        _destroy: false,
      })),
    });
  }

  productDialogRef.value?.open?.();
};

const openOrderDialog = order => {
  resetOrderForm();

  if (order) {
    editingOrderId.value = order.id;
    Object.assign(orderForm, {
      reference: order.reference,
      productId: order.productId || '',
      productVariantId: order.productVariantId || '',
      contactId: order.contactId || '',
      conversationId: order.conversationId || '',
      contactName: order.contactName,
      contactPhone: order.contactPhone || '',
      contactEmail: order.contactEmail || '',
      address: order.address || '',
      location: order.location || '',
      trackingNumber: order.trackingNumber || '',
      paymentMethod: order.paymentMethod || '',
      paymentStatus: order.paymentStatus || 'pending',
      paymentProofUrl: order.paymentProofUrl || '',
      discountAmount: Number(order.discountAmount || 0),
      shippingAmount: Number(order.shippingAmount || 0),
      paidAmount: Number(order.paidAmount || 0),
      responsibleAgentId: order.responsibleAgent?.id || '',
      fulfillmentNotes: order.fulfillmentNotes || '',
      quantity: order.quantity,
      status: order.status,
      source: order.source,
      notes: order.notes || '',
      actions:
        order.productOrderActions?.length > 0
          ? order.productOrderActions.map(action => ({
              id: action.id,
              localKey: nextActionKey(),
              name: action.name,
              actionType: action.actionType,
              triggerStatus: action.triggerStatus || 'manual',
              templateKey: templateSelectionForPayload(action.templatePayload),
              templatePayload: action.templatePayload || {},
              content: action.content || '',
              status: action.status || 'pending',
              completeOrder: !!action.completeOrder,
            }))
          : [createDefaultActionForm()],
    });

    if (order.contact) {
      appendOrderContactOption(normalizedOrderContactOption(order.contact));
      selectedOrderContactId.value = String(order.contact.id);
    } else if (order.contactId && order.contactName) {
      appendOrderContactOption({
        value: String(order.contactId),
        label: order.contactEmail
          ? `${order.contactName} (${order.contactEmail})`
          : order.contactPhone
            ? `${order.contactName} (${order.contactPhone})`
            : order.contactName,
        name: order.contactName,
        email: order.contactEmail || '',
        phoneNumber: order.contactPhone || '',
        location: order.location || '',
        address: order.address || '',
      });
      selectedOrderContactId.value = String(order.contactId);
    }
  }

  orderDialogRef.value?.open?.();
  primeOrderContactSearch();
  nextTick(() => {
    orderForm.actions.forEach(action => {
      if (action.actionType === 'template' && action.templateKey) {
        syncTemplateParserState(action);
      }
    });
  });
};

const openOrderDetails = order => {
  orderDetailDialogRef.value?.open?.(order);
};

const clearOrderFilters = () => {
  Object.assign(orderFilters, {
    search: '',
    status: 'all',
    productId: 'all',
    dateFrom: '',
    dateTo: '',
  });
};

const buildProductPayload = () => ({
  name: productForm.name.trim(),
  sku: productForm.sku.trim(),
  category: productForm.category.trim(),
  source_type: productForm.sourceType,
  source: productForm.source.trim(),
  price: Number(productForm.price || 0),
  currency: productForm.currency.trim() || 'USD',
  active: !!productForm.active,
  description: productForm.description.trim(),
  track_stock: !!productForm.trackStock,
  stock_quantity: Number(productForm.stockQuantity || 0),
  low_stock_threshold: Number(productForm.lowStockThreshold || 0),
  allow_backorder: !!productForm.allowBackorder,
  gallery_image_urls: productForm.galleryImageUrls
    .map(url => url?.toString().trim())
    .filter(Boolean),
  automation_rules: {
    default_actions: configuredActionsPayloadFor(
      productForm.automationRulesDefaultActions
    ),
  },
  product_variants_attributes: productForm.variants
    .filter(
      variant =>
        variant._destroy ||
        variant.id ||
        variant.name.trim() ||
        variant.sku.trim() ||
        variant.size.trim() ||
        variant.color.trim() ||
        variant.presentation.trim()
    )
    .map((variant, index) => ({
      ...(variant.id ? { id: variant.id } : {}),
      name: variant.name.trim(),
      sku: variant.sku.trim(),
      size: variant.size.trim(),
      color: variant.color.trim(),
      presentation: variant.presentation.trim(),
      price: Number(variant.price || 0),
      stock_quantity: Number(variant.stockQuantity || 0),
      low_stock_threshold: Number(variant.lowStockThreshold || 0),
      active: !!variant.active,
      position: index + 1,
      _destroy: !!variant._destroy,
    })),
});

const buildOrderActionsPayload = () => {
  const actions = orderForm.actions
    .filter(isActionPayloadComplete)
    .map((action, index) => {
      const payload = {
        ...(action.id ? { id: action.id } : {}),
        position: index + 1,
        name: action.name.trim(),
        action_type: action.actionType,
        status: action.status || 'pending',
        trigger_status: action.triggerStatus || 'manual',
        complete_order: !!action.completeOrder,
      };

      if (action.actionType === 'template') {
        const parser = templateParserRefs.value[action.localKey];
        const template = selectedTemplateForAction(action);
        const processedParams =
          parser?.processedParams ||
          action.templatePayload?.processed_params ||
          {};

        return {
          ...payload,
          content:
            parser?.renderedTemplate ||
            action.content ||
            t('PRODUCTS_ORDERS.LABELS.TEMPLATE_READY'),
          template_payload: template
            ? {
                name: template.name,
                category: template.category,
                language: template.language,
                namespace: template.namespace,
                processed_params: processedParams,
              }
            : action.templatePayload || {},
        };
      }

      return {
        ...payload,
        content: action.content.trim(),
        template_payload: {},
      };
    });

  const deletedActions = deletedActionIds.value.map(id => ({
    id,
    _destroy: true,
  }));

  return [...actions, ...deletedActions];
};

const buildOrderPayload = () => ({
  reference: orderForm.reference.trim(),
  product_id: orderForm.productId || null,
  product_variant_id: orderForm.productVariantId || null,
  contact_id: orderForm.contactId || null,
  conversation_id: orderForm.conversationId || null,
  contact_name: orderForm.contactName.trim(),
  contact_phone: orderForm.contactPhone.trim(),
  contact_email: orderForm.contactEmail.trim(),
  address: orderForm.address.trim(),
  location: orderForm.location.trim(),
  tracking_number: orderForm.trackingNumber.trim(),
  payment_method: orderForm.paymentMethod.trim(),
  payment_status: orderForm.paymentStatus,
  payment_proof_url: orderForm.paymentProofUrl.trim(),
  discount_amount: Number(orderForm.discountAmount || 0),
  shipping_amount: Number(orderForm.shippingAmount || 0),
  paid_amount: Number(orderForm.paidAmount || 0),
  responsible_agent_id: orderForm.responsibleAgentId || null,
  fulfillment_notes: orderForm.fulfillmentNotes.trim(),
  quantity: Number(orderForm.quantity || 1),
  status: orderForm.status,
  source: orderForm.source.trim(),
  notes: orderForm.notes.trim(),
  product_order_actions_attributes: buildOrderActionsPayload(),
});

const buildConfiguredActionPayload = action => {
  const payload = {
    name: action.name.trim(),
    action_type: action.actionType,
    trigger_status: action.triggerStatus || 'manual',
    complete_order: !!action.completeOrder,
  };

  if (action.actionType === 'template') {
    const template = availableWhatsAppTemplates.value.find(
      item => buildTemplateOptionValue(item) === action.templateKey
    );

    return {
      ...payload,
      content:
        action.content?.trim() ||
        template?.name ||
        t('PRODUCTS_ORDERS.LABELS.TEMPLATE_READY'),
      template_payload: template
        ? {
            name: template.name,
            category: template.category,
            language: template.language,
            namespace: template.namespace,
          }
        : {},
    };
  }

  return {
    ...payload,
    content: action.content.trim(),
    template_payload: {},
  };
};

const submitModuleConfig = async () => {
  const paymentMethods = moduleConfigForm.paymentMethods
    .map(method => method?.toString().trim())
    .filter(Boolean);
  const webhookUrls = moduleConfigForm.webhookUrls
    .map(url => url?.toString().trim())
    .filter(Boolean);
  const defaultActions = configuredActionsPayloadFor(
    moduleConfigForm.defaultActions
  );
  const stuckOrderRules = moduleConfigForm.stuckOrderRules
    .filter(rule => rule.status && Number(rule.hours) > 0)
    .map(rule => ({
      status: rule.status,
      hours: Number(rule.hours),
      message: rule.message?.toString().trim() || '',
      notify_responsible: !!rule.notifyResponsible,
      create_conversation_note: !!rule.createConversationNote,
    }));

  try {
    isSavingModuleConfig.value = true;
    await updateAccount(
      {
        products_orders_config: {
          payment_methods: paymentMethods,
          default_actions: defaultActions,
          webhook_urls: webhookUrls,
          webhook_events: moduleConfigForm.webhookEvents,
          stuck_order_rules: stuckOrderRules,
        },
      },
      { silent: true }
    );
    useAlert(t('PRODUCTS_ORDERS.ALERTS.CONFIG_UPDATED'));
    settingsDialogRef.value?.close?.();
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        error?.message ||
        t('PRODUCTS_ORDERS.ALERTS.CONFIG_UPDATE_ERROR')
    );
  } finally {
    isSavingModuleConfig.value = false;
  }
};

const submitProduct = async () => {
  try {
    const payload = buildProductPayload();
    if (editingProductId.value) {
      await store.dispatch('productsOrders/updateProduct', {
        id: editingProductId.value,
        ...payload,
      });
      useAlert(t('PRODUCTS_ORDERS.ALERTS.PRODUCT_UPDATED'));
    } else {
      await store.dispatch('productsOrders/createProduct', payload);
      useAlert(t('PRODUCTS_ORDERS.ALERTS.PRODUCT_CREATED'));
    }
    productDialogRef.value?.close?.();
    resetProductForm();
  } catch (error) {
    useAlert(error?.message || t('PRODUCTS_ORDERS.ALERTS.PRODUCT_UPDATE_ERROR'));
  }
};

const submitOrder = async () => {
  try {
    const payload = buildOrderPayload();
    if (editingOrderId.value) {
      await store.dispatch('productsOrders/updateProductOrder', {
        id: editingOrderId.value,
        ...payload,
      });
      useAlert(t('PRODUCTS_ORDERS.ALERTS.ORDER_UPDATED'));
    } else {
      await store.dispatch('productsOrders/createProductOrder', payload);
      useAlert(t('PRODUCTS_ORDERS.ALERTS.ORDER_CREATED'));
    }
    orderDialogRef.value?.close?.();
    resetOrderForm();
  } catch (error) {
    useAlert(error?.message || t('PRODUCTS_ORDERS.ALERTS.ORDER_UPDATE_ERROR'));
  }
};

const deleteProduct = async product => {
  openConfirmationDialog({
    title: t('PRODUCTS_ORDERS.CONFIRMATIONS.DELETE_PRODUCT_TITLE'),
    message: t('PRODUCTS_ORDERS.PRODUCTS.DELETE_CONFIRM'),
    confirmLabel: t('CHOICE_TOGGLE.YES'),
    action: async () => {
      try {
        await store.dispatch('productsOrders/deleteProduct', product.id);
        useAlert(t('PRODUCTS_ORDERS.ALERTS.PRODUCT_DELETED'));
      } catch (error) {
        useAlert(
          error?.message || t('PRODUCTS_ORDERS.ALERTS.PRODUCT_UPDATE_ERROR')
        );
      }
    },
  });
};

const deleteOrder = async order => {
  openConfirmationDialog({
    title: t('PRODUCTS_ORDERS.CONFIRMATIONS.DELETE_ORDER_TITLE'),
    message: t('PRODUCTS_ORDERS.ORDERS.DELETE_CONFIRM'),
    confirmLabel: t('CHOICE_TOGGLE.YES'),
    action: async () => {
      try {
        await store.dispatch('productsOrders/deleteProductOrder', order.id);
        useAlert(t('PRODUCTS_ORDERS.ALERTS.ORDER_DELETED'));
      } catch (error) {
        useAlert(error?.message || t('PRODUCTS_ORDERS.ALERTS.ORDER_UPDATE_ERROR'));
      }
    },
  });
};

const advanceOrder = async order => {
  openConfirmationDialog({
    title: t('PRODUCTS_ORDERS.CONFIRMATIONS.ADVANCE_ORDER_TITLE'),
    message: t('PRODUCTS_ORDERS.CONFIRMATIONS.ADVANCE_ORDER_MESSAGE'),
    confirmLabel: t('CHOICE_TOGGLE.YES'),
    tone: 'edit',
    action: async () => {
      try {
        await store.dispatch('productsOrders/advanceProductOrder', order.id);
        useAlert(t('PRODUCTS_ORDERS.ALERTS.ORDER_ADVANCED'));
      } catch (error) {
        useAlert(error?.message || t('PRODUCTS_ORDERS.ALERTS.ORDER_UPDATE_ERROR'));
      }
    },
  });
};

const completeOrder = async order => {
  openConfirmationDialog({
    title: t('PRODUCTS_ORDERS.CONFIRMATIONS.COMPLETE_ORDER_TITLE'),
    message: t('PRODUCTS_ORDERS.CONFIRMATIONS.COMPLETE_ORDER_MESSAGE'),
    confirmLabel: t('CHOICE_TOGGLE.YES'),
    tone: 'edit',
    action: async () => {
      try {
        await store.dispatch('productsOrders/completeProductOrder', order.id);
        useAlert(t('PRODUCTS_ORDERS.ALERTS.ORDER_COMPLETED'));
      } catch (error) {
        useAlert(error?.message || t('PRODUCTS_ORDERS.ALERTS.ORDER_UPDATE_ERROR'));
      }
    },
  });
};

const cancelOrder = async order => {
  openConfirmationDialog({
    title: t('PRODUCTS_ORDERS.CONFIRMATIONS.CANCEL_ORDER_TITLE'),
    message: t('PRODUCTS_ORDERS.CONFIRMATIONS.CANCEL_ORDER_MESSAGE'),
    confirmLabel: t('CHOICE_TOGGLE.YES'),
    action: async () => {
      try {
        await store.dispatch('productsOrders/cancelProductOrder', order.id);
        useAlert(t('PRODUCTS_ORDERS.ALERTS.ORDER_CANCELED'));
      } catch (error) {
        useAlert(error?.message || t('PRODUCTS_ORDERS.ALERTS.ORDER_UPDATE_ERROR'));
      }
    },
  });
};

const openLinkedConversation = order => {
  if (!order.conversationId) {
    return;
  }

  router.push({
    name: 'inbox_conversation',
    params: {
      accountId: route.params.accountId,
      conversation_id: order.conversationId,
    },
  });
};

const executeAction = async action => {
  try {
    await store.dispatch('productsOrders/executeProductOrderAction', action.id);
    useAlert(t('PRODUCTS_ORDERS.ALERTS.ACTION_EXECUTED'));
  } catch (error) {
    useAlert(error?.message || t('PRODUCTS_ORDERS.ALERTS.ACTION_EXECUTE_ERROR'));
    throw error;
  }
};

const handleKanbanDragStart = order => {
  draggedOrderId.value = order.id;
};

const handleKanbanDragEnd = () => {
  draggedOrderId.value = null;
  hoveredKanbanColumn.value = '';
};

const handleKanbanDrop = async status => {
  const draggedOrder = filteredProductOrders.value.find(
    order => Number(order.id) === Number(draggedOrderId.value)
  );

  hoveredKanbanColumn.value = '';

  if (!draggedOrder || draggedOrder.status === status) {
    draggedOrderId.value = null;
    return;
  }

  try {
    await store.dispatch('productsOrders/updateProductOrder', {
      id: draggedOrder.id,
      status,
    });
    useAlert(
      t('PRODUCTS_ORDERS.ALERTS.ORDER_MOVED_TO', {
        status: statusLabel(status),
      })
    );
  } catch (error) {
    // Store toast handles API errors
  } finally {
    draggedOrderId.value = null;
  }
};

const confirmExecution = async () => {
  if (!pendingExecution.value.action) {
    return;
  }

  try {
    await executeAction(pendingExecution.value.action);
    executionPreviewDialogRef.value?.close?.();
  } catch (error) {
    // Store toast handles API errors
  }
};

onMounted(() => {
  store.dispatch('inboxes/get');
  store.dispatch('agents/get');
  store.dispatch('productsOrders/getProducts');
  store.dispatch('productsOrders/getProductOrders');
  applyConversationPrefill();
});

watch(
  () => orderForm.productId,
  () => {
    orderForm.productVariantId = '';
    if (!editingOrderId.value) {
      orderForm.actions = buildInitialOrderActions(orderForm.productId);
    }
  }
);

watch(
  selectedOrderContact,
  contact => {
    if (!contact) {
      return;
    }

    orderForm.contactId = contact.value;
    orderForm.contactName = contact.name || orderForm.contactName;
    orderForm.contactPhone = contact.phoneNumber || orderForm.contactPhone;
    orderForm.contactEmail = contact.email || orderForm.contactEmail;
    orderForm.location = contact.location || orderForm.location;
    orderForm.address = contact.address || orderForm.address;
  }
);

watch(selectedOrderContactId, contactId => {
  if (!contactId) {
    orderForm.contactId = '';
  }
});

watch(
  orderPaymentStatusPreview,
  paymentStatus => {
    if (['failed', 'refunded'].includes(orderForm.paymentStatus)) {
      return;
    }

    orderForm.paymentStatus = paymentStatus;
  }
);

watch(
  () => orderForm.conversationId,
  conversationIdValue => {
    if (suppressConversationContextWatch.value) {
      return;
    }
    syncConversationContext(conversationIdValue);
  }
);

watch(
  () => [route.query.openOrder, route.query.conversationId],
  () => {
    applyConversationPrefill();
  }
);

watch(
  productsOrdersConfig,
  () => {
    resetModuleConfigForm();
  },
  { immediate: true, deep: true }
);
</script>

<template>
  <div class="flex h-full min-w-0 w-full flex-col overflow-hidden bg-n-surface-1">
    <div class="flex min-h-0 flex-1 w-full flex-col gap-6 overflow-y-auto px-6 py-6">
      <div
        class="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between"
      >
        <div class="space-y-1">
          <h1 class="text-2xl font-semibold text-n-slate-12">
            {{ $t('PRODUCTS_ORDERS.TITLE') }}
          </h1>
          <p class="mb-0 text-sm text-n-slate-11">
            {{ $t('PRODUCTS_ORDERS.DESCRIPTION') }}
          </p>
        </div>
        <div class="flex flex-wrap gap-2">
          <Button
            variant="faded"
            color="slate"
            icon="i-lucide-settings-2"
            :label="$t('PRODUCTS_ORDERS.BUTTONS.CONFIGURE')"
            sm
            @click="openModuleSettingsDialog"
          />
          <Button
            variant="faded"
            color="slate"
            icon="i-lucide-package-plus"
            :label="$t('PRODUCTS_ORDERS.BUTTONS.ADD_PRODUCT')"
            sm
            @click="openProductDialog()"
          />
          <Button
            icon="i-lucide-plus"
            :label="$t('PRODUCTS_ORDERS.BUTTONS.ADD_ORDER')"
            sm
            @click="openOrderDialog()"
          />
        </div>
      </div>

      <div class="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <div
          v-for="card in summaryCards"
          :key="card.key"
          class="rounded-xl border border-n-weak bg-n-solid-1 p-4 shadow-sm"
        >
          <p class="mb-1 text-sm font-medium text-n-slate-11">
            {{ card.label }}
          </p>
          <p class="mb-0 text-3xl font-semibold text-n-slate-12">
            {{ card.value }}
          </p>
        </div>
      </div>

      <div class="rounded-2xl border border-n-weak bg-n-solid-1 shadow-sm">
        <div
          class="flex flex-wrap items-center justify-between gap-3 border-b border-n-weak px-4 py-4"
        >
          <div class="flex flex-wrap gap-2">
            <Button
              :variant="activeTab === 'orders' ? 'solid' : 'faded'"
              :color="activeTab === 'orders' ? 'blue' : 'slate'"
              sm
              :label="$t('PRODUCTS_ORDERS.TABS.ORDERS')"
              @click="activeTab = 'orders'"
            />
            <Button
              :variant="activeTab === 'products' ? 'solid' : 'faded'"
              :color="activeTab === 'products' ? 'blue' : 'slate'"
              sm
              :label="$t('PRODUCTS_ORDERS.TABS.PRODUCTS')"
              @click="activeTab = 'products'"
            />
          </div>
          <div v-if="activeTab === 'orders'" class="flex flex-wrap gap-2">
            <Button
              :variant="ordersView === 'list' ? 'solid' : 'faded'"
              :color="ordersView === 'list' ? 'blue' : 'slate'"
              sm
              icon="i-lucide-list"
              :label="$t('PRODUCTS_ORDERS.VIEWS.LIST')"
              @click="ordersView = 'list'"
            />
            <Button
              :variant="ordersView === 'kanban' ? 'solid' : 'faded'"
              :color="ordersView === 'kanban' ? 'blue' : 'slate'"
              sm
              icon="i-lucide-columns-3"
              :label="$t('PRODUCTS_ORDERS.VIEWS.KANBAN')"
              @click="ordersView = 'kanban'"
            />
          </div>
        </div>

        <div
          v-if="activeTab === 'orders' && productOrders.length"
          class="grid gap-3 border-b border-n-weak px-4 py-4 md:grid-cols-2 xl:grid-cols-6"
        >
          <Input
            v-model="orderFilters.search"
            :label="$t('PRODUCTS_ORDERS.FILTERS.SEARCH_LABEL')"
            :placeholder="$t('PRODUCTS_ORDERS.FILTERS.SEARCH_PLACEHOLDER')"
          />

          <div class="flex min-w-0 flex-col gap-1">
            <label class="mb-0.5 text-heading-3 text-n-slate-12">
              {{ $t('PRODUCTS_ORDERS.FILTERS.STATUS_LABEL') }}
            </label>
            <select
              v-model="orderFilters.status"
              class="h-10 rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-n-weak transition-all duration-200 hover:outline-n-slate-6 focus:outline-n-brand"
            >
              <option
                v-for="option in orderFilterStatusOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>

          <div class="flex min-w-0 flex-col gap-1">
            <label class="mb-0.5 text-heading-3 text-n-slate-12">
              {{ $t('PRODUCTS_ORDERS.FILTERS.PRODUCT_LABEL') }}
            </label>
            <select
              v-model="orderFilters.productId"
              class="h-10 rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-n-weak transition-all duration-200 hover:outline-n-slate-6 focus:outline-n-brand"
            >
              <option
                v-for="option in orderFilterProductOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>

          <div class="max-w-44">
            <Input
              v-model="orderFilters.dateFrom"
              type="date"
              :label="$t('PRODUCTS_ORDERS.FILTERS.DATE_FROM_LABEL')"
            />
          </div>

          <div class="max-w-44">
            <Input
              v-model="orderFilters.dateTo"
              type="date"
              :label="$t('PRODUCTS_ORDERS.FILTERS.DATE_TO_LABEL')"
            />
          </div>

          <div class="flex items-end">
            <Button
              v-if="hasActiveOrderFilters"
              variant="ghost"
              color="slate"
              sm
              icon="i-lucide-x"
              class="shrink-0"
              :label="$t('PRODUCTS_ORDERS.FILTERS.CLEAR')"
              @click="clearOrderFilters"
            />
          </div>
        </div>

        <div
          v-if="activeTab === 'orders' && filteredProductOrders.length"
          class="grid gap-3 border-b border-n-weak px-4 py-4 md:grid-cols-2 xl:grid-cols-4"
        >
          <div
            v-for="item in filteredOrderAnalytics"
            :key="item.key"
            class="rounded-xl border border-n-weak bg-n-alpha-1 p-4"
          >
            <p class="mb-1 text-xs uppercase tracking-[0.08em] text-n-slate-10">
              {{ item.label }}
            </p>
            <p class="mb-1 text-xl font-semibold text-n-slate-12">
              {{ item.value }}
            </p>
            <p class="mb-0 text-xs text-n-slate-11">
              {{ item.helper }}
            </p>
          </div>
        </div>

        <div
          v-if="isLoading"
          class="flex min-h-[20rem] items-center justify-center"
        >
          <Spinner class="text-n-brand" />
        </div>

        <div
          v-else-if="activeTab === 'products' && !products.length"
          class="flex min-h-[20rem] flex-col items-center justify-center gap-2 px-6 py-12 text-center"
        >
          <span
            class="inline-flex size-12 items-center justify-center rounded-full bg-n-brand/10 text-n-brand"
          >
            <Icon icon="i-lucide-package" class="size-6" />
          </span>
          <h2 class="text-lg font-semibold text-n-slate-12">
            {{ $t('PRODUCTS_ORDERS.PRODUCTS.EMPTY_TITLE') }}
          </h2>
          <p class="mb-0 max-w-lg text-sm text-n-slate-11">
            {{ $t('PRODUCTS_ORDERS.PRODUCTS.EMPTY_DESCRIPTION') }}
          </p>
        </div>

        <div
          v-else-if="activeTab === 'orders' && !productOrders.length"
          class="flex min-h-[20rem] flex-col items-center justify-center gap-2 px-6 py-12 text-center"
        >
          <span
            class="inline-flex size-12 items-center justify-center rounded-full bg-n-brand/10 text-n-brand"
          >
            <Icon icon="i-lucide-shopping-bag" class="size-6" />
          </span>
          <h2 class="text-lg font-semibold text-n-slate-12">
            {{ $t('PRODUCTS_ORDERS.ORDERS.EMPTY_TITLE') }}
          </h2>
          <p class="mb-0 max-w-lg text-sm text-n-slate-11">
            {{ $t('PRODUCTS_ORDERS.ORDERS.EMPTY_DESCRIPTION') }}
          </p>
        </div>

        <div
          v-else-if="activeTab === 'orders' && !filteredProductOrders.length"
          class="flex min-h-[20rem] flex-col items-center justify-center gap-2 px-6 py-12 text-center"
        >
          <span
            class="inline-flex size-12 items-center justify-center rounded-full bg-n-brand/10 text-n-brand"
          >
            <Icon icon="i-lucide-filter-x" class="size-6" />
          </span>
          <h2 class="text-lg font-semibold text-n-slate-12">
            {{ $t('PRODUCTS_ORDERS.FILTERS.EMPTY_TITLE') }}
          </h2>
          <p class="mb-0 max-w-lg text-sm text-n-slate-11">
            {{ $t('PRODUCTS_ORDERS.FILTERS.EMPTY_DESCRIPTION') }}
          </p>
          <Button
            variant="faded"
            color="slate"
            sm
            icon="i-lucide-rotate-ccw"
            :label="$t('PRODUCTS_ORDERS.FILTERS.CLEAR')"
            @click="clearOrderFilters"
          />
        </div>

        <div
          v-else-if="activeTab === 'products'"
          class="grid gap-4 p-4 md:grid-cols-2 xl:grid-cols-3"
        >
          <article
            v-for="product in products"
            :key="product.id"
            class="flex flex-col gap-4 rounded-xl border border-n-weak bg-n-surface-1 p-4"
          >
            <div
              v-if="productPrimaryImage(product)"
              class="overflow-hidden rounded-xl border border-n-weak bg-n-alpha-1"
            >
              <img
                :src="productPrimaryImage(product)"
                :alt="product.name"
                class="h-44 w-full object-cover"
              />
            </div>
            <div class="flex items-start justify-between gap-3">
              <div class="space-y-1">
                <h3 class="mb-0 text-base font-semibold text-n-slate-12">
                  {{ product.name }}
                </h3>
                <p class="mb-0 text-sm text-n-slate-10">
                  {{
                    product.sku || $t('PRODUCTS_ORDERS.LABELS.NOT_AVAILABLE')
                  }}
                </p>
              </div>
              <span
                class="inline-flex rounded-full border px-2 py-1 text-xs font-semibold"
                :class="
                  product.active
                    ? statusBadgeClass('completed')
                    : statusBadgeClass('canceled')
                "
              >
                {{
                  product.active
                    ? $t('PRODUCTS_ORDERS.PRODUCTS.ACTIVE')
                    : $t('PRODUCTS_ORDERS.PRODUCTS.INACTIVE')
                }}
              </span>
            </div>

            <div class="flex flex-wrap gap-2 text-xs text-n-slate-11">
              <span class="rounded-full bg-n-alpha-2 px-2 py-1">
                {{
                  product.sourceType === 'catalog'
                    ? $t('PRODUCTS_ORDERS.PRODUCTS.TYPE_CATALOG')
                    : $t('PRODUCTS_ORDERS.PRODUCTS.TYPE_PRODUCT')
                }}
              </span>
              <span class="rounded-full bg-n-alpha-2 px-2 py-1">
                {{ product.source }}
              </span>
              <span
                v-if="product.category"
                class="rounded-full bg-n-alpha-2 px-2 py-1"
              >
                {{ product.category }}
              </span>
              <span
                class="inline-flex rounded-full border px-2 py-1 font-semibold"
                :class="inventoryStatusClass(product.inventoryStatus)"
              >
                {{ inventoryStatusLabel(product.inventoryStatus) }}
              </span>
              <span
                v-if="hasProductVariants(product)"
                class="rounded-full bg-n-alpha-2 px-2 py-1"
              >
                {{
                  $t('PRODUCTS_ORDERS.VARIANTS.COUNT', {
                    count: product.productVariants.filter(variant => variant.active)
                      .length,
                  })
                }}
              </span>
              <span
                v-if="productGalleryImages(product).length"
                class="rounded-full bg-n-alpha-2 px-2 py-1"
              >
                {{
                  $t('PRODUCTS_ORDERS.PRODUCTS.GALLERY_COUNT', {
                    count: productGalleryImages(product).length,
                  })
                }}
              </span>
              <span
                v-if="productDefaultActionsCount(product)"
                class="rounded-full bg-n-alpha-2 px-2 py-1"
              >
                {{
                  $t('PRODUCTS_ORDERS.AUTOMATIONS.PRODUCT_DEFAULT_ACTIONS_COUNT', {
                    count: productDefaultActionsCount(product),
                  })
                }}
              </span>
            </div>

            <p class="mb-0 text-lg font-semibold text-n-slate-12">
              {{ formatPrice(product) }}
            </p>
            <p class="mb-0 text-sm text-n-slate-11">
              {{ productInventorySummary(product) }}
            </p>
            <p class="mb-0 flex-1 text-sm text-n-slate-11">
              {{
                product.description ||
                $t('PRODUCTS_ORDERS.LABELS.NOT_AVAILABLE')
              }}
            </p>

            <div class="flex flex-wrap justify-end gap-2">
              <Button
                variant="ghost"
                color="slate"
                sm
                icon="i-lucide-pencil"
                :label="$t('PRODUCTS_ORDERS.ACTIONS.EDIT')"
                @click="openProductDialog(product)"
              />
              <Button
                variant="ghost"
                color="ruby"
                sm
                icon="i-lucide-trash-2"
                :label="$t('PRODUCTS_ORDERS.ACTIONS.DELETE')"
                @click="deleteProduct(product)"
              />
            </div>
          </article>
        </div>

        <div
          v-else-if="ordersView === 'list'"
          class="flex flex-col gap-4 p-4"
        >
          <div
            class="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-dashed border-n-weak px-4 py-3 text-sm text-n-slate-11"
          >
            <span>
              {{
                $t('PRODUCTS_ORDERS.FILTERS.RESULTS', {
                  count: filteredProductOrders.length,
                })
              }}
            </span>
            <Button
              v-if="hasActiveOrderFilters"
              variant="ghost"
              color="slate"
              sm
              icon="i-lucide-x"
              :label="$t('PRODUCTS_ORDERS.FILTERS.CLEAR')"
              @click="clearOrderFilters"
            />
          </div>
          <article
            v-for="order in filteredProductOrders"
            :key="order.id"
            class="rounded-xl border border-n-weak bg-n-surface-1 p-4"
          >
            <div
              class="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between"
            >
              <div class="space-y-2">
                <div class="flex flex-wrap items-center gap-2">
                  <h3 class="mb-0 text-lg font-semibold text-n-slate-12">
                    {{ order.reference }}
                  </h3>
                  <span
                    class="inline-flex rounded-full border px-2 py-1 text-xs font-semibold"
                    :class="statusBadgeClass(order.status)"
                  >
                    {{ statusLabel(order.status) }}
                  </span>
                  <span
                    class="inline-flex rounded-full border px-2 py-1 text-xs font-semibold"
                    :class="paymentStatusBadgeClass(order.paymentStatus)"
                  >
                    {{ paymentStatusLabel(order.paymentStatus) }}
                  </span>
                </div>
                <p class="mb-0 text-sm font-medium text-n-slate-12">
                  {{ productNameForOrder(order) }}
                </p>
                <div
                  class="grid gap-2 text-sm text-n-slate-11 md:grid-cols-2 xl:grid-cols-4"
                >
                  <p class="mb-0">
                    <span class="font-medium text-n-slate-12">{{
                      $t('PRODUCTS_ORDERS.FIELDS.CONTACT_NAME')
                    }}</span>
                    : {{ order.contactName }}
                  </p>
                  <p class="mb-0">
                    <span class="font-medium text-n-slate-12">{{
                      $t('PRODUCTS_ORDERS.FIELDS.CONTACT_PHONE')
                    }}</span>
                    :
                    {{
                      order.contactPhone ||
                      $t('PRODUCTS_ORDERS.LABELS.NOT_AVAILABLE')
                    }}
                  </p>
                  <p class="mb-0">
                    <span class="font-medium text-n-slate-12">{{
                      $t('PRODUCTS_ORDERS.FIELDS.CONTACT_EMAIL')
                    }}</span>
                    :
                    {{
                      order.contactEmail ||
                      $t('PRODUCTS_ORDERS.LABELS.NOT_AVAILABLE')
                    }}
                  </p>
                  <p class="mb-0">
                    <span class="font-medium text-n-slate-12">{{
                      $t('PRODUCTS_ORDERS.FIELDS.SOURCE')
                    }}</span>
                    : {{ order.source }}
                  </p>
                  <p class="mb-0">
                    <span class="font-medium text-n-slate-12">{{
                      $t('PRODUCTS_ORDERS.LABELS.CONVERSATION')
                    }}</span>
                    :
                    {{
                      order.conversationId ||
                      $t('PRODUCTS_ORDERS.LABELS.NOT_AVAILABLE')
                    }}
                  </p>
                  <p class="mb-0">
                    <span class="font-medium text-n-slate-12">{{
                      $t('PRODUCTS_ORDERS.FIELDS.QUANTITY')
                    }}</span>
                    :
                    {{
                      $t('PRODUCTS_ORDERS.LABELS.QUANTITY', {
                        count: order.quantity,
                      })
                    }}
                  </p>
                  <p class="mb-0">
                    <span class="font-medium text-n-slate-12">{{
                      $t('PRODUCTS_ORDERS.LABELS.CREATED_AT')
                    }}</span>
                    : {{ formatDateTime(order.createdAt) }}
                  </p>
                  <p class="mb-0 md:col-span-2">
                    <span class="font-medium text-n-slate-12">{{
                      $t('PRODUCTS_ORDERS.FIELDS.ADDRESS')
                    }}</span>
                    :
                    {{
                      order.address ||
                      $t('PRODUCTS_ORDERS.LABELS.NOT_AVAILABLE')
                    }}
                  </p>
                  <p class="mb-0">
                    <span class="font-medium text-n-slate-12">{{
                      $t('PRODUCTS_ORDERS.FIELDS.LOCATION')
                    }}</span>
                    :
                    {{
                      order.location ||
                      $t('PRODUCTS_ORDERS.LABELS.NOT_AVAILABLE')
                    }}
                  </p>
                  <p class="mb-0">
                    <span class="font-medium text-n-slate-12">{{
                      $t('PRODUCTS_ORDERS.FIELDS.TRACKING_NUMBER')
                    }}</span>
                    :
                    {{
                      order.trackingNumber ||
                      $t('PRODUCTS_ORDERS.LABELS.NOT_AVAILABLE')
                    }}
                  </p>
                  <p class="mb-0">
                    <span class="font-medium text-n-slate-12">{{
                      $t('PRODUCTS_ORDERS.FIELDS.PAYMENT_METHOD')
                    }}</span>
                    :
                    {{
                      order.paymentMethod ||
                      $t('PRODUCTS_ORDERS.LABELS.NOT_AVAILABLE')
                    }}
                  </p>
                  <p class="mb-0">
                    <span class="font-medium text-n-slate-12">{{
                      $t('PRODUCTS_ORDERS.FIELDS.TOTAL_AMOUNT')
                    }}</span>
                    :
                    {{ formatCurrencyAmount(order.totalAmount, order.product?.currency) }}
                  </p>
                  <p class="mb-0">
                    <span class="font-medium text-n-slate-12">{{
                      $t('PRODUCTS_ORDERS.FIELDS.OUTSTANDING_AMOUNT')
                    }}</span>
                    :
                    {{
                      formatCurrencyAmount(
                        order.outstandingAmount,
                        order.product?.currency
                      )
                    }}
                  </p>
                  <p class="mb-0">
                    <span class="font-medium text-n-slate-12">{{
                      $t('PRODUCTS_ORDERS.FIELDS.RESPONSIBLE_AGENT')
                    }}</span>
                    :
                    {{
                      order.responsibleAgent?.name ||
                      $t('PRODUCTS_ORDERS.LABELS.NOT_AVAILABLE')
                    }}
                  </p>
                  <p class="mb-0 md:col-span-2">
                    <span class="font-medium text-n-slate-12">{{
                      $t('PRODUCTS_ORDERS.FIELDS.NOTES')
                    }}</span>
                    :
                    {{
                      order.notes || $t('PRODUCTS_ORDERS.LABELS.NO_NOTES')
                    }}
                  </p>
                  <p class="mb-0 md:col-span-2">
                    <span class="font-medium text-n-slate-12">{{
                      $t('PRODUCTS_ORDERS.FIELDS.FULFILLMENT_NOTES')
                    }}</span>
                    :
                    {{
                      order.fulfillmentNotes ||
                      $t('PRODUCTS_ORDERS.LABELS.NO_NOTES')
                    }}
                  </p>
                </div>
              </div>

              <div class="flex flex-wrap gap-2 xl:justify-end">
                <Button
                  v-if="nextStatusForOrder(order)"
                  variant="faded"
                  color="blue"
                  sm
                  icon="i-lucide-forward"
                  :label="
                    $t('PRODUCTS_ORDERS.ORDERS.ADVANCE_TO', {
                      status: statusLabel(nextStatusForOrder(order)),
                    })
                  "
                  @click="advanceOrder(order)"
                />
                <Button
                  v-if="canCancelOrder(order)"
                  variant="faded"
                  color="ruby"
                  sm
                  icon="i-lucide-ban"
                  :label="$t('PRODUCTS_ORDERS.ORDERS.CANCEL')"
                  @click="cancelOrder(order)"
                />
                <Button
                  v-if="!productsOrdersFeatures.advancedStatuses && !isTerminalOrderStatus(order)"
                  variant="faded"
                  color="teal"
                  sm
                  icon="i-lucide-check-check"
                  :label="$t('PRODUCTS_ORDERS.ORDERS.COMPLETE')"
                  @click="completeOrder(order)"
                />
                <Button
                  v-if="
                    productsOrdersFeatures.openConversationLink &&
                    order.conversationId
                  "
                  variant="ghost"
                  color="slate"
                  sm
                  icon="i-lucide-messages-square"
                  :label="$t('PRODUCTS_ORDERS.ORDERS.OPEN_CONVERSATION')"
                  @click="openLinkedConversation(order)"
                />
                <Button
                  variant="ghost"
                  color="slate"
                  sm
                  icon="i-lucide-eye"
                  :label="$t('PRODUCTS_ORDERS.BUTTONS.VIEW_DETAILS')"
                  @click="openOrderDetails(order)"
                />
                <Button
                  variant="ghost"
                  color="slate"
                  sm
                  icon="i-lucide-pencil"
                  :label="$t('PRODUCTS_ORDERS.ACTIONS.EDIT')"
                  @click="openOrderDialog(order)"
                />
                <Button
                  variant="ghost"
                  color="ruby"
                  sm
                  icon="i-lucide-trash-2"
                  :label="$t('PRODUCTS_ORDERS.ACTIONS.DELETE')"
                  @click="deleteOrder(order)"
                />
              </div>
            </div>

            <div class="mt-4 rounded-xl border border-n-weak bg-n-alpha-1 p-4">
              <div class="mb-3 flex items-center justify-between gap-2">
                <h4 class="mb-0 text-sm font-semibold text-n-slate-12">
                  {{ $t('PRODUCTS_ORDERS.ORDERS.ACTIONS_TITLE') }}
                </h4>
                <span class="text-xs text-n-slate-10">
                  {{ order.productOrderActions?.length || 0 }}
                </span>
              </div>

              <div
                v-if="!order.productOrderActions?.length"
                class="rounded-lg border border-dashed border-n-weak px-3 py-6 text-center text-sm text-n-slate-10"
              >
                {{ $t('PRODUCTS_ORDERS.ORDERS.NO_ACTIONS') }}
              </div>

              <div v-else class="flex flex-col gap-3">
                <div
                  v-for="(action, index) in order.productOrderActions"
                  :key="action.id"
                  class="rounded-lg border border-n-weak bg-n-solid-1 p-3"
                >
                  <div
                    class="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between"
                  >
                    <div class="space-y-1">
                      <div class="flex flex-wrap items-center gap-2">
                        <span
                          class="text-xs font-semibold uppercase tracking-[0.08em] text-n-slate-10"
                        >
                          {{
                            $t('PRODUCTS_ORDERS.LABELS.POSITION', {
                              count: index + 1,
                            })
                          }}
                        </span>
                        <span
                          class="inline-flex rounded-full border px-2 py-1 text-xs font-semibold"
                          :class="statusBadgeClass(action.status)"
                        >
                          {{ statusLabel(action.status) }}
                        </span>
                      </div>
                      <p class="mb-0 text-sm font-semibold text-n-slate-12">
                        {{ action.name }}
                      </p>
                      <p class="mb-0 text-sm text-n-slate-11">
                        {{ actionTypeLabel(action.actionType) }}
                      </p>
                      <p class="mb-0 text-sm text-n-slate-11">
                        {{
                          action.triggerStatus === 'manual'
                            ? $t('PRODUCTS_ORDERS.ACTIONS.TRIGGER_MANUAL')
                            : $t('PRODUCTS_ORDERS.ACTIONS.TRIGGER_ON_STATUS', {
                                status: statusLabel(action.triggerStatus),
                              })
                        }}
                      </p>
                      <p class="mb-0 text-sm text-n-slate-11">
                        {{ executeActionLabel(action) }}
                      </p>
                      <p
                        v-if="
                          ['message', 'template'].includes(action.actionType) &&
                          !order.conversationId
                        "
                        class="mb-0 text-xs text-n-ruby-11"
                      >
                        {{
                          $t(
                            'PRODUCTS_ORDERS.ORDERS.ACTION_REQUIRES_CONVERSATION'
                          )
                        }}
                      </p>
                    </div>

                    <div class="flex flex-wrap gap-2">
                      <Button
                        variant="faded"
                        color="blue"
                        sm
                        icon="i-lucide-send"
                        :disabled="!canExecuteAction(order, action)"
                        :label="$t('PRODUCTS_ORDERS.ACTIONS.RUN')"
                        @click="openExecutionPreview(order, action)"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="productsOrdersFeatures.timeline"
              class="mt-4 rounded-xl border border-n-weak bg-n-solid-1 p-4"
            >
              <div class="mb-3 flex items-center justify-between gap-2">
                <h4 class="mb-0 text-sm font-semibold text-n-slate-12">
                  {{ $t('PRODUCTS_ORDERS.TIMELINE.TITLE') }}
                </h4>
                <span class="text-xs text-n-slate-10">
                  {{ order.productOrderEvents?.length || 0 }}
                </span>
              </div>

              <div
                v-if="!order.productOrderEvents?.length"
                class="rounded-lg border border-dashed border-n-weak px-3 py-6 text-center text-sm text-n-slate-10"
              >
                {{ $t('PRODUCTS_ORDERS.TIMELINE.EMPTY') }}
              </div>

              <div v-else class="flex flex-col gap-3">
                <div
                  v-for="event in order.productOrderEvents"
                  :key="event.id"
                  class="rounded-lg border border-n-weak bg-n-alpha-1 p-3"
                >
                  <p class="mb-1 text-sm font-medium text-n-slate-12">
                    {{ timelineDescription(event) }}
                  </p>
                  <p class="mb-0 text-xs text-n-slate-10">
                    {{ formatDateTime(event.occurredAt) }}
                  </p>
                </div>
              </div>
            </div>
          </article>
        </div>

        <div
          v-else
          class="flex min-h-[32rem] w-full gap-4 overflow-x-auto p-4"
        >
          <section
            v-for="column in kanbanColumns"
            :key="column.status"
            class="flex min-w-[20rem] flex-1 basis-[20rem] flex-col rounded-2xl border border-n-weak bg-n-alpha-1 transition"
            :class="{
              'border-n-brand bg-n-brand/5': hoveredKanbanColumn === column.status,
            }"
            @dragover.prevent="hoveredKanbanColumn = column.status"
            @dragleave="hoveredKanbanColumn = ''"
            @drop.prevent="handleKanbanDrop(column.status)"
          >
            <header class="border-b border-n-weak px-4 py-3">
              <div class="flex items-center justify-between gap-2">
                <p class="mb-0 text-sm font-semibold text-n-slate-12">
                  {{ column.label }}
                </p>
                <span
                  class="inline-flex rounded-full border px-2 py-1 text-xs font-semibold"
                  :class="statusBadgeClass(column.status)"
                >
                  {{ column.orders.length }}
                </span>
              </div>
            </header>

            <div class="flex flex-1 flex-col gap-3 p-3">
              <div
                v-if="!column.orders.length"
                class="flex min-h-[8rem] items-center justify-center rounded-xl border border-dashed border-n-weak px-3 text-center text-sm text-n-slate-10"
              >
                {{ $t('PRODUCTS_ORDERS.KANBAN.EMPTY_COLUMN') }}
              </div>

              <button
                v-for="order in column.orders"
                :key="order.id"
                type="button"
                class="w-full rounded-xl border border-n-weak bg-n-surface-1 p-3 text-left transition hover:border-n-slate-6"
                draggable="true"
                @dragstart="handleKanbanDragStart(order)"
                @dragend="handleKanbanDragEnd"
                @click="openOrderDetails(order)"
              >
                <div class="flex items-start justify-between gap-2">
                  <div class="space-y-1">
                    <p class="mb-0 text-sm font-semibold text-n-slate-12">
                      {{ order.reference }}
                    </p>
                    <p class="mb-0 text-sm text-n-slate-11">
                      {{ productNameForOrder(order) }}
                    </p>
                  </div>
                  <Icon
                    icon="i-lucide-arrow-up-right"
                    class="size-4 text-n-slate-10"
                  />
                </div>
                <div class="mt-3 flex flex-wrap gap-2">
                  <span
                    class="inline-flex rounded-full border px-2 py-1 text-[11px] font-semibold"
                    :class="paymentStatusBadgeClass(order.paymentStatus)"
                  >
                    {{ paymentStatusLabel(order.paymentStatus) }}
                  </span>
                </div>
                <div class="mt-3 space-y-1 text-xs text-n-slate-10">
                  <p class="mb-0">{{ order.contactName }}</p>
                  <p class="mb-0">
                    {{
                      $t('PRODUCTS_ORDERS.LABELS.QUANTITY', {
                        count: order.quantity,
                      })
                    }}
                  </p>
                  <p class="mb-0">
                    {{
                      order.responsibleAgent?.name ||
                      $t('PRODUCTS_ORDERS.LABELS.NOT_AVAILABLE')
                    }}
                  </p>
                  <p class="mb-0">
                    {{
                      formatCurrencyAmount(
                        order.totalAmount,
                        order.product?.currency
                      )
                    }}
                  </p>
                </div>
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>

    <Dialog
      ref="productDialogRef"
      width="lg"
      :title="
        editingProductId
          ? $t('PRODUCTS_ORDERS.PRODUCTS.EDIT')
          : $t('PRODUCTS_ORDERS.PRODUCTS.ADD')
      "
      :confirm-button-label="
        editingProductId
          ? $t('PRODUCTS_ORDERS.ACTIONS.SAVE')
          : $t('PRODUCTS_ORDERS.ACTIONS.CREATE')
      "
      :is-loading="isProductSubmitting"
      :disable-confirm-button="!productForm.name.trim()"
      overflow-y-auto
      @confirm="submitProduct"
      @close="resetProductForm"
    >
      <div class="grid gap-4">
        <div class="grid gap-4 md:grid-cols-2">
          <Input
            v-model="productForm.name"
            :label="$t('PRODUCTS_ORDERS.FIELDS.NAME')"
            required
          />
          <Input
            v-model="productForm.sku"
            :label="$t('PRODUCTS_ORDERS.FIELDS.SKU')"
          />
          <Input
            v-model="productForm.category"
            :label="$t('PRODUCTS_ORDERS.FIELDS.CATEGORY')"
          />
          <div class="flex min-w-0 flex-col gap-1">
            <label class="mb-0.5 text-heading-3 text-n-slate-12">
              {{ $t('PRODUCTS_ORDERS.FIELDS.SOURCE_TYPE') }}
            </label>
            <select
              v-model="productForm.sourceType"
              class="h-10 rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-n-weak transition-all duration-200 hover:outline-n-slate-6 focus:outline-n-brand"
            >
              <option
                v-for="option in productTypeOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
          <Input
            v-model="productForm.source"
            :label="$t('PRODUCTS_ORDERS.FIELDS.SOURCE')"
            required
          />
          <Input
            v-model="productForm.price"
            type="number"
            min="0"
            :label="$t('PRODUCTS_ORDERS.FIELDS.PRICE')"
          />
          <Input
            v-model="productForm.currency"
            :label="$t('PRODUCTS_ORDERS.FIELDS.CURRENCY')"
          />
          <label
            class="flex items-center gap-2 text-sm font-medium text-n-slate-12 md:col-span-2"
          >
            <input
              v-model="productForm.active"
              type="checkbox"
              class="size-4 rounded border-n-weak text-n-brand focus:ring-n-brand"
            />
            {{ $t('PRODUCTS_ORDERS.FIELDS.ACTIVE') }}
          </label>
          <TextArea
            v-model="productForm.description"
            :label="$t('PRODUCTS_ORDERS.FIELDS.DESCRIPTION')"
            :max-length="2000"
            class="md:col-span-2"
          />
        </div>

        <div class="rounded-xl border border-n-weak bg-n-alpha-1 p-4">
          <div class="mb-4 flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 class="mb-0 text-sm font-semibold text-n-slate-12">
                {{ $t('PRODUCTS_ORDERS.PRODUCTS.GALLERY_TITLE') }}
              </h3>
              <p class="mb-0 text-xs text-n-slate-10">
                {{ $t('PRODUCTS_ORDERS.PRODUCTS.GALLERY_DESCRIPTION') }}
              </p>
            </div>
            <Button
              variant="faded"
              color="slate"
              sm
              icon="i-lucide-image-plus"
              :label="$t('PRODUCTS_ORDERS.PRODUCTS.ADD_GALLERY_IMAGE')"
              @click="addGalleryImageRow"
            />
          </div>

          <div class="flex flex-col gap-3">
            <div
              v-for="(imageUrl, index) in productForm.galleryImageUrls"
              :key="`gallery-${index}`"
              class="grid gap-3 rounded-xl border border-n-weak bg-n-solid-1 p-3 md:grid-cols-[minmax(0,1fr)_auto]"
            >
              <div class="space-y-3">
                <Input
                  v-model="productForm.galleryImageUrls[index]"
                  :label="$t('PRODUCTS_ORDERS.FIELDS.IMAGE_URL')"
                  :placeholder="$t('PRODUCTS_ORDERS.PRODUCTS.GALLERY_PLACEHOLDER')"
                />
                <div
                  v-if="imageUrl?.trim()"
                  class="overflow-hidden rounded-xl border border-n-weak bg-n-alpha-1"
                >
                  <img
                    :src="imageUrl"
                    :alt="`${productForm.name || 'product'}-${index + 1}`"
                    class="h-40 w-full object-cover"
                  />
                </div>
              </div>
              <div class="flex items-start justify-end">
                <Button
                  variant="ghost"
                  color="ruby"
                  sm
                  icon="i-lucide-trash-2"
                  :label="$t('PRODUCTS_ORDERS.ACTIONS.REMOVE')"
                  @click="removeGalleryImageRow(index)"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="rounded-xl border border-n-weak bg-n-alpha-1 p-4">
          <div class="mb-4 flex items-center justify-between gap-3">
            <div>
              <h3 class="mb-0 text-sm font-semibold text-n-slate-12">
                {{ $t('PRODUCTS_ORDERS.INVENTORY.TITLE') }}
              </h3>
              <p class="mb-0 text-xs text-n-slate-10">
                {{ $t('PRODUCTS_ORDERS.INVENTORY.DESCRIPTION') }}
              </p>
            </div>
          </div>
          <div class="grid gap-4 md:grid-cols-2">
            <label
              class="flex items-center gap-2 text-sm font-medium text-n-slate-12 md:col-span-2"
            >
              <input
                v-model="productForm.trackStock"
                type="checkbox"
                class="size-4 rounded border-n-weak text-n-brand focus:ring-n-brand"
              />
              {{ $t('PRODUCTS_ORDERS.FIELDS.TRACK_STOCK') }}
            </label>
            <Input
              v-model="productForm.stockQuantity"
              type="number"
              min="0"
              :disabled="!productForm.trackStock || visibleProductVariants.length > 0"
              :label="$t('PRODUCTS_ORDERS.FIELDS.STOCK_QUANTITY')"
            />
            <Input
              v-model="productForm.lowStockThreshold"
              type="number"
              min="0"
              :disabled="!productForm.trackStock || visibleProductVariants.length > 0"
              :label="$t('PRODUCTS_ORDERS.FIELDS.LOW_STOCK_THRESHOLD')"
            />
            <label
              class="flex items-center gap-2 text-sm font-medium text-n-slate-12 md:col-span-2"
            >
              <input
                v-model="productForm.allowBackorder"
                type="checkbox"
                class="size-4 rounded border-n-weak text-n-brand focus:ring-n-brand"
              />
              {{ $t('PRODUCTS_ORDERS.FIELDS.ALLOW_BACKORDER') }}
            </label>
          </div>
        </div>

        <div class="rounded-xl border border-n-weak bg-n-alpha-1 p-4">
          <div class="mb-4 flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 class="mb-0 text-sm font-semibold text-n-slate-12">
                {{ $t('PRODUCTS_ORDERS.VARIANTS.TITLE') }}
              </h3>
              <p class="mb-0 text-xs text-n-slate-10">
                {{ $t('PRODUCTS_ORDERS.VARIANTS.DESCRIPTION') }}
              </p>
            </div>
            <Button
              variant="faded"
              color="slate"
              sm
              icon="i-lucide-plus"
              :label="$t('PRODUCTS_ORDERS.VARIANTS.ADD')"
              @click="addVariantRow"
            />
          </div>

          <div
            v-if="!visibleProductVariants.length"
            class="rounded-lg border border-dashed border-n-weak px-3 py-6 text-center text-sm text-n-slate-10"
          >
            {{ $t('PRODUCTS_ORDERS.VARIANTS.EMPTY') }}
          </div>

          <div v-else class="flex flex-col gap-4">
            <div
              v-for="(variant, index) in visibleProductVariants"
              :key="variant.localKey"
              class="rounded-xl border border-n-weak bg-n-solid-1 p-4"
            >
              <div class="mb-3 flex items-center justify-between gap-2">
                <p class="mb-0 text-sm font-semibold text-n-slate-12">
                  {{
                    $t('PRODUCTS_ORDERS.VARIANTS.VARIANT_NUMBER', {
                      count: index + 1,
                    })
                  }}
                </p>
                <Button
                  variant="ghost"
                  color="ruby"
                  sm
                  icon="i-lucide-trash-2"
                  :label="$t('PRODUCTS_ORDERS.ACTIONS.REMOVE')"
                  @click="removeVariantRow(variant)"
                />
              </div>

              <div class="grid gap-4 md:grid-cols-2">
                <Input
                  v-model="variant.name"
                  :label="$t('PRODUCTS_ORDERS.FIELDS.VARIANT_NAME')"
                  required
                />
                <Input
                  v-model="variant.sku"
                  :label="$t('PRODUCTS_ORDERS.FIELDS.SKU')"
                />
                <Input
                  v-model="variant.size"
                  :label="$t('PRODUCTS_ORDERS.FIELDS.SIZE')"
                />
                <Input
                  v-model="variant.color"
                  :label="$t('PRODUCTS_ORDERS.FIELDS.COLOR')"
                />
                <Input
                  v-model="variant.presentation"
                  :label="$t('PRODUCTS_ORDERS.FIELDS.PRESENTATION')"
                />
                <Input
                  v-model="variant.price"
                  type="number"
                  min="0"
                  :label="$t('PRODUCTS_ORDERS.FIELDS.PRICE')"
                />
                <Input
                  v-model="variant.stockQuantity"
                  type="number"
                  min="0"
                  :disabled="!productForm.trackStock"
                  :label="$t('PRODUCTS_ORDERS.FIELDS.STOCK_QUANTITY')"
                />
                <Input
                  v-model="variant.lowStockThreshold"
                  type="number"
                  min="0"
                  :disabled="!productForm.trackStock"
                  :label="$t('PRODUCTS_ORDERS.FIELDS.LOW_STOCK_THRESHOLD')"
                />
                <label
                  class="flex items-center gap-2 text-sm font-medium text-n-slate-12 md:col-span-2"
                >
                  <input
                    v-model="variant.active"
                    type="checkbox"
                    class="size-4 rounded border-n-weak text-n-brand focus:ring-n-brand"
                  />
                  {{ $t('PRODUCTS_ORDERS.FIELDS.ACTIVE') }}
                </label>
              </div>
            </div>
          </div>
        </div>

        <div class="rounded-xl border border-n-weak bg-n-alpha-1 p-4">
          <div class="mb-4 flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 class="mb-0 text-sm font-semibold text-n-slate-12">
                {{ $t('PRODUCTS_ORDERS.AUTOMATIONS.PRODUCT_TITLE') }}
              </h3>
              <p class="mb-0 text-xs text-n-slate-10">
                {{ $t('PRODUCTS_ORDERS.AUTOMATIONS.PRODUCT_DESCRIPTION') }}
              </p>
            </div>
            <Button
              variant="faded"
              color="slate"
              sm
              icon="i-lucide-plus"
              :label="$t('PRODUCTS_ORDERS.ACTIONS.ADD')"
              @click="addProductAutomationActionRow"
            />
          </div>

          <div
            v-if="!productForm.automationRulesDefaultActions.length"
            class="rounded-lg border border-dashed border-n-weak px-3 py-6 text-center text-sm text-n-slate-10"
          >
            {{ $t('PRODUCTS_ORDERS.AUTOMATIONS.PRODUCT_EMPTY') }}
          </div>

          <div v-else class="flex flex-col gap-4">
            <div
              v-for="(configAction, index) in productForm.automationRulesDefaultActions"
              :key="configAction.localKey"
              class="rounded-xl border border-n-weak bg-n-solid-1 p-4"
            >
              <div class="mb-3 flex items-center justify-between gap-2">
                <p class="mb-0 text-sm font-semibold text-n-slate-12">
                  {{
                    $t('PRODUCTS_ORDERS.LABELS.POSITION', {
                      count: index + 1,
                    })
                  }}
                </p>
                <Button
                  variant="ghost"
                  color="ruby"
                  sm
                  icon="i-lucide-trash-2"
                  :label="$t('PRODUCTS_ORDERS.ACTIONS.REMOVE')"
                  @click="removeProductAutomationActionRow(configAction)"
                />
              </div>

              <div class="grid gap-4 md:grid-cols-2">
                <Input
                  v-model="configAction.name"
                  :label="$t('PRODUCTS_ORDERS.ACTIONS.NAME')"
                  required
                />
                <div class="flex min-w-0 flex-col gap-1">
                  <label class="mb-0.5 text-heading-3 text-n-slate-12">
                    {{ $t('PRODUCTS_ORDERS.ACTIONS.TYPE') }}
                  </label>
                  <select
                    v-model="configAction.actionType"
                    class="h-10 rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-n-weak transition-all duration-200 hover:outline-n-slate-6 focus:outline-n-brand"
                  >
                    <option
                      v-for="option in actionTypeOptions"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                </div>
                <div class="flex min-w-0 flex-col gap-1">
                  <label class="mb-0.5 text-heading-3 text-n-slate-12">
                    {{ $t('PRODUCTS_ORDERS.ACTIONS.TRIGGER') }}
                  </label>
                  <select
                    v-model="configAction.triggerStatus"
                    class="h-10 rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-n-weak transition-all duration-200 hover:outline-n-slate-6 focus:outline-n-brand"
                  >
                    <option
                      v-for="option in actionTriggerOptions"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                </div>
                <TextArea
                  v-if="configAction.actionType !== 'template'"
                  v-model="configAction.content"
                  :label="$t('PRODUCTS_ORDERS.ACTIONS.CONTENT')"
                  :max-length="5000"
                  class="md:col-span-2"
                />
                <div v-else class="md:col-span-2">
                  <label class="mb-0.5 block text-heading-3 text-n-slate-12">
                    {{ $t('PRODUCTS_ORDERS.ACTIONS.TEMPLATE') }}
                  </label>
                  <ComboBox
                    v-model="configAction.templateKey"
                    :options="buildTemplateOptions(availableWhatsAppTemplates)"
                    :placeholder="$t('PRODUCTS_ORDERS.ACTIONS.SELECT_TEMPLATE')"
                    :empty-state="$t('PRODUCTS_ORDERS.ACTIONS.NO_TEMPLATES')"
                    @update:model-value="
                      value => {
                        configAction.templateKey = value;
                        configAction.templatePayload = {};
                      }
                    "
                  />
                </div>
                <label
                  class="flex items-center gap-2 text-sm font-medium text-n-slate-12 md:col-span-2"
                >
                  <input
                    v-model="configAction.completeOrder"
                    type="checkbox"
                    class="size-4 rounded border-n-weak text-n-brand focus:ring-n-brand"
                  />
                  {{ $t('PRODUCTS_ORDERS.ACTIONS.MARK_COMPLETE_ORDER') }}
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>

    <Dialog
      ref="executionPreviewDialogRef"
      width="lg"
      :title="$t('PRODUCTS_ORDERS.ACTIONS.EXECUTE_PREVIEW_TITLE')"
      :description="$t('PRODUCTS_ORDERS.ACTIONS.EXECUTE_PREVIEW_DESCRIPTION')"
      :confirm-button-label="$t('PRODUCTS_ORDERS.ACTIONS.CONFIRM_RUN')"
      :cancel-button-label="$t('DIALOG.BUTTONS.CANCEL')"
      :is-loading="uiFlags.isExecutingProductOrderAction"
      @confirm="confirmExecution"
      @close="resetExecutionPreview"
    >
      <div class="grid gap-4">
        <div class="grid gap-3 rounded-xl border border-n-weak bg-n-alpha-1 p-4 md:grid-cols-2">
          <p class="mb-0 text-sm text-n-slate-11">
            <span class="font-medium text-n-slate-12">{{
              $t('PRODUCTS_ORDERS.FIELDS.REFERENCE')
            }}</span>
            :
            {{
              pendingExecution.order?.reference ||
              $t('PRODUCTS_ORDERS.LABELS.NOT_AVAILABLE')
            }}
          </p>
          <p class="mb-0 text-sm text-n-slate-11">
            <span class="font-medium text-n-slate-12">{{
              $t('PRODUCTS_ORDERS.LABELS.ACTION')
            }}</span>
            :
            {{
              pendingExecution.action?.name ||
              $t('PRODUCTS_ORDERS.LABELS.NOT_AVAILABLE')
            }}
          </p>
          <p class="mb-0 text-sm text-n-slate-11">
            <span class="font-medium text-n-slate-12">{{
              $t('PRODUCTS_ORDERS.ACTIONS.TYPE')
            }}</span>
            :
            {{
              pendingExecution.action
                ? actionTypeLabel(pendingExecution.action.actionType)
                : $t('PRODUCTS_ORDERS.LABELS.NOT_AVAILABLE')
            }}
          </p>
          <p class="mb-0 text-sm text-n-slate-11">
            <span class="font-medium text-n-slate-12">{{
              $t('PRODUCTS_ORDERS.LABELS.CONVERSATION')
            }}</span>
            :
            {{
              pendingExecution.order?.conversationId ||
              $t('PRODUCTS_ORDERS.LABELS.NOT_AVAILABLE')
            }}
          </p>
          <p
            v-if="pendingExecution.action?.actionType === 'template'"
            class="mb-0 text-sm text-n-slate-11 md:col-span-2"
          >
            <span class="font-medium text-n-slate-12">{{
              $t('PRODUCTS_ORDERS.LABELS.TEMPLATE_NAME')
            }}</span>
            :
            {{
              pendingExecution.action?.templatePayload?.name ||
              $t('PRODUCTS_ORDERS.LABELS.NOT_AVAILABLE')
            }}
          </p>
        </div>

        <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
          <p class="mb-2 text-sm font-medium text-n-slate-12">
            {{ $t('PRODUCTS_ORDERS.LABELS.PREVIEW') }}
          </p>
          <div class="whitespace-pre-wrap break-words text-sm text-n-slate-11">
            {{ executionPreviewContent }}
          </div>
        </div>
      </div>
    </Dialog>

    <Dialog
      ref="confirmationDialogRef"
      width="md"
      :type="pendingConfirmation.tone"
      :title="pendingConfirmation.title"
      :description="pendingConfirmation.message"
      :confirm-button-label="
        pendingConfirmation.confirmLabel || $t('CHOICE_TOGGLE.YES')
      "
      :cancel-button-label="$t('CHOICE_TOGGLE.NO')"
      @confirm="confirmPendingAction"
      @close="resetConfirmationDialog"
    />

    <Dialog
      ref="settingsDialogRef"
      width="2xl"
      :title="$t('PRODUCTS_ORDERS.CONFIG.TITLE')"
      :description="$t('PRODUCTS_ORDERS.CONFIG.DESCRIPTION')"
      :confirm-button-label="$t('PRODUCTS_ORDERS.ACTIONS.SAVE')"
      :cancel-button-label="$t('DIALOG.BUTTONS.CANCEL')"
      :is-loading="isSavingModuleConfig"
      :submit-on-enter="false"
      overflow-y-auto
      @confirm="submitModuleConfig"
      @close="resetModuleConfigForm"
    >
      <div class="grid gap-4">
        <div class="rounded-xl border border-n-weak bg-n-alpha-1 p-4">
          <div class="mb-4 flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 class="mb-0 text-sm font-semibold text-n-slate-12">
                {{ $t('PRODUCTS_ORDERS.CONFIG.PAYMENT_METHODS_TITLE') }}
              </h3>
              <p class="mb-0 text-xs text-n-slate-10">
                {{ $t('PRODUCTS_ORDERS.CONFIG.PAYMENT_METHODS_DESCRIPTION') }}
              </p>
            </div>
            <Button
              variant="faded"
              color="slate"
              sm
              icon="i-lucide-plus"
              :label="$t('PRODUCTS_ORDERS.CONFIG.ADD_PAYMENT_METHOD')"
              @click="addPaymentMethodRow"
            />
          </div>

          <div class="flex flex-col gap-3">
            <div
              v-for="(paymentMethod, index) in moduleConfigForm.paymentMethods"
              :key="`payment-method-${index}`"
              class="flex items-end gap-2 rounded-xl border border-n-weak bg-n-solid-1 p-3"
            >
              <Input
                v-model="moduleConfigForm.paymentMethods[index]"
                :label="$t('PRODUCTS_ORDERS.FIELDS.PAYMENT_METHOD')"
                :placeholder="$t('PRODUCTS_ORDERS.CONFIG.PAYMENT_METHOD_PLACEHOLDER')"
              />
              <Button
                variant="ghost"
                color="ruby"
                sm
                icon="i-lucide-trash-2"
                :label="$t('PRODUCTS_ORDERS.ACTIONS.REMOVE')"
                @click="removePaymentMethodRow(index)"
              />
            </div>
          </div>
        </div>

        <div class="rounded-xl border border-n-weak bg-n-alpha-1 p-4">
          <div class="mb-4 flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 class="mb-0 text-sm font-semibold text-n-slate-12">
                {{ $t('PRODUCTS_ORDERS.CONFIG.DEFAULT_ACTIONS_TITLE') }}
              </h3>
              <p class="mb-0 text-xs text-n-slate-10">
                {{ $t('PRODUCTS_ORDERS.CONFIG.DEFAULT_ACTIONS_DESCRIPTION') }}
              </p>
            </div>
            <Button
              variant="faded"
              color="slate"
              sm
              icon="i-lucide-plus"
              :label="$t('PRODUCTS_ORDERS.ACTIONS.ADD')"
              @click="addConfiguredActionRow"
            />
          </div>

          <div
            v-if="!moduleConfigForm.defaultActions.length"
            class="rounded-lg border border-dashed border-n-weak px-3 py-6 text-center text-sm text-n-slate-10"
          >
            {{ $t('PRODUCTS_ORDERS.CONFIG.NO_DEFAULT_ACTIONS') }}
          </div>

          <div v-else class="flex flex-col gap-4">
            <div
              v-for="(configAction, index) in moduleConfigForm.defaultActions"
              :key="configAction.localKey"
              class="rounded-xl border border-n-weak bg-n-solid-1 p-4"
            >
              <div class="mb-3 flex items-center justify-between gap-2">
                <p class="mb-0 text-sm font-semibold text-n-slate-12">
                  {{
                    $t('PRODUCTS_ORDERS.LABELS.POSITION', {
                      count: index + 1,
                    })
                  }}
                </p>
                <Button
                  variant="ghost"
                  color="ruby"
                  sm
                  icon="i-lucide-trash-2"
                  :label="$t('PRODUCTS_ORDERS.ACTIONS.REMOVE')"
                  @click="removeConfiguredActionRow(configAction)"
                />
              </div>

              <div class="grid gap-4 md:grid-cols-2">
                <Input
                  v-model="configAction.name"
                  :label="$t('PRODUCTS_ORDERS.ACTIONS.NAME')"
                  required
                />
                <div class="flex min-w-0 flex-col gap-1">
                  <label class="mb-0.5 text-heading-3 text-n-slate-12">
                    {{ $t('PRODUCTS_ORDERS.ACTIONS.TYPE') }}
                  </label>
                  <select
                    v-model="configAction.actionType"
                    class="h-10 rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-n-weak transition-all duration-200 hover:outline-n-slate-6 focus:outline-n-brand"
                  >
                    <option
                      v-for="option in actionTypeOptions"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                </div>
                <div class="flex min-w-0 flex-col gap-1">
                  <label class="mb-0.5 text-heading-3 text-n-slate-12">
                    {{ $t('PRODUCTS_ORDERS.ACTIONS.TRIGGER') }}
                  </label>
                  <select
                    v-model="configAction.triggerStatus"
                    class="h-10 rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-n-weak transition-all duration-200 hover:outline-n-slate-6 focus:outline-n-brand"
                  >
                    <option
                      v-for="option in actionTriggerOptions"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                </div>
                <TextArea
                  v-if="configAction.actionType !== 'template'"
                  v-model="configAction.content"
                  :label="$t('PRODUCTS_ORDERS.ACTIONS.CONTENT')"
                  :max-length="5000"
                  class="md:col-span-2"
                />
                <div v-else class="md:col-span-2">
                  <label class="mb-0.5 block text-heading-3 text-n-slate-12">
                    {{ $t('PRODUCTS_ORDERS.ACTIONS.TEMPLATE') }}
                  </label>
                  <ComboBox
                    v-model="configAction.templateKey"
                    :options="buildTemplateOptions(availableWhatsAppTemplates)"
                    :placeholder="$t('PRODUCTS_ORDERS.ACTIONS.SELECT_TEMPLATE')"
                    :empty-state="$t('PRODUCTS_ORDERS.ACTIONS.NO_TEMPLATES')"
                    @update:model-value="
                      value => {
                        configAction.templateKey = value;
                        configAction.templatePayload = {};
                      }
                    "
                  />
                </div>
                <label
                  class="flex items-center gap-2 text-sm font-medium text-n-slate-12 md:col-span-2"
                >
                  <input
                    v-model="configAction.completeOrder"
                    type="checkbox"
                    class="size-4 rounded border-n-weak text-n-brand focus:ring-n-brand"
                  />
                  {{ $t('PRODUCTS_ORDERS.ACTIONS.MARK_COMPLETE_ORDER') }}
                </label>
              </div>
            </div>
          </div>
        </div>

        <div class="rounded-xl border border-n-weak bg-n-alpha-1 p-4">
          <div class="mb-4 flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 class="mb-0 text-sm font-semibold text-n-slate-12">
                {{ $t('PRODUCTS_ORDERS.AUTOMATIONS.WEBHOOKS_TITLE') }}
              </h3>
              <p class="mb-0 text-xs text-n-slate-10">
                {{ $t('PRODUCTS_ORDERS.AUTOMATIONS.WEBHOOKS_DESCRIPTION') }}
              </p>
            </div>
            <Button
              variant="faded"
              color="slate"
              sm
              icon="i-lucide-link"
              :label="$t('PRODUCTS_ORDERS.AUTOMATIONS.ADD_WEBHOOK_URL')"
              @click="addWebhookUrlRow"
            />
          </div>

          <div class="mb-4 grid gap-2 md:grid-cols-2 xl:grid-cols-3">
            <label
              v-for="option in automationWebhookEventOptions"
              :key="option.value"
              class="flex items-center gap-2 rounded-xl border border-n-weak bg-n-solid-1 px-3 py-3 text-sm text-n-slate-12"
            >
              <input
                :checked="moduleConfigForm.webhookEvents.includes(option.value)"
                type="checkbox"
                class="size-4 rounded border-n-weak text-n-brand focus:ring-n-brand"
                @change="
                  event => {
                    moduleConfigForm.webhookEvents = event.target.checked
                      ? Array.from(
                          new Set([
                            ...moduleConfigForm.webhookEvents,
                            option.value,
                          ])
                        )
                      : moduleConfigForm.webhookEvents.filter(
                          value => value !== option.value
                        );
                  }
                "
              />
              {{ option.label }}
            </label>
          </div>

          <div class="flex flex-col gap-3">
            <div
              v-for="(webhookUrl, index) in moduleConfigForm.webhookUrls"
              :key="`webhook-url-${index}`"
              class="flex items-end gap-2 rounded-xl border border-n-weak bg-n-solid-1 p-3"
            >
              <Input
                v-model="moduleConfigForm.webhookUrls[index]"
                :label="$t('PRODUCTS_ORDERS.AUTOMATIONS.WEBHOOK_URL')"
                :placeholder="$t('PRODUCTS_ORDERS.AUTOMATIONS.WEBHOOK_URL_PLACEHOLDER')"
              />
              <Button
                variant="ghost"
                color="ruby"
                sm
                icon="i-lucide-trash-2"
                :label="$t('PRODUCTS_ORDERS.ACTIONS.REMOVE')"
                @click="removeWebhookUrlRow(index)"
              />
            </div>
          </div>
        </div>

        <div class="rounded-xl border border-n-weak bg-n-alpha-1 p-4">
          <div class="mb-4 flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 class="mb-0 text-sm font-semibold text-n-slate-12">
                {{ $t('PRODUCTS_ORDERS.AUTOMATIONS.STUCK_RULES_TITLE') }}
              </h3>
              <p class="mb-0 text-xs text-n-slate-10">
                {{ $t('PRODUCTS_ORDERS.AUTOMATIONS.STUCK_RULES_DESCRIPTION') }}
              </p>
            </div>
            <Button
              variant="faded"
              color="slate"
              sm
              icon="i-lucide-bell-plus"
              :label="$t('PRODUCTS_ORDERS.AUTOMATIONS.ADD_STUCK_RULE')"
              @click="addStuckOrderRuleRow"
            />
          </div>

          <div
            v-if="!moduleConfigForm.stuckOrderRules.length"
            class="rounded-lg border border-dashed border-n-weak px-3 py-6 text-center text-sm text-n-slate-10"
          >
            {{ $t('PRODUCTS_ORDERS.AUTOMATIONS.STUCK_RULES_EMPTY') }}
          </div>

          <div v-else class="flex flex-col gap-4">
            <div
              v-for="rule in moduleConfigForm.stuckOrderRules"
              :key="rule.localKey"
              class="rounded-xl border border-n-weak bg-n-solid-1 p-4"
            >
              <div class="mb-3 flex items-center justify-between gap-2">
                <p class="mb-0 text-sm font-semibold text-n-slate-12">
                  {{ $t('PRODUCTS_ORDERS.AUTOMATIONS.STUCK_RULE') }}
                </p>
                <Button
                  variant="ghost"
                  color="ruby"
                  sm
                  icon="i-lucide-trash-2"
                  :label="$t('PRODUCTS_ORDERS.ACTIONS.REMOVE')"
                  @click="removeStuckOrderRuleRow(rule)"
                />
              </div>

              <div class="grid gap-4 md:grid-cols-2">
                <div class="flex min-w-0 flex-col gap-1">
                  <label class="mb-0.5 text-heading-3 text-n-slate-12">
                    {{ $t('PRODUCTS_ORDERS.AUTOMATIONS.STATUS') }}
                  </label>
                  <select
                    v-model="rule.status"
                    class="h-10 rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-n-weak transition-all duration-200 hover:outline-n-slate-6 focus:outline-n-brand"
                  >
                    <option
                      v-for="option in automationReminderStatusOptions"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                </div>

                <Input
                  v-model="rule.hours"
                  type="number"
                  min="1"
                  :label="$t('PRODUCTS_ORDERS.AUTOMATIONS.HOURS')"
                />

                <TextArea
                  v-model="rule.message"
                  :label="$t('PRODUCTS_ORDERS.AUTOMATIONS.MESSAGE')"
                  :max-length="500"
                  class="md:col-span-2"
                />

                <label
                  class="flex items-center gap-2 text-sm font-medium text-n-slate-12"
                >
                  <input
                    v-model="rule.notifyResponsible"
                    type="checkbox"
                    class="size-4 rounded border-n-weak text-n-brand focus:ring-n-brand"
                  />
                  {{ $t('PRODUCTS_ORDERS.AUTOMATIONS.NOTIFY_RESPONSIBLE') }}
                </label>

                <label
                  class="flex items-center gap-2 text-sm font-medium text-n-slate-12"
                >
                  <input
                    v-model="rule.createConversationNote"
                    type="checkbox"
                    class="size-4 rounded border-n-weak text-n-brand focus:ring-n-brand"
                  />
                  {{ $t('PRODUCTS_ORDERS.AUTOMATIONS.CREATE_CONVERSATION_NOTE') }}
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>

    <Dialog
      ref="orderDialogRef"
      width="2xl"
      :title="
        editingOrderId
          ? $t('PRODUCTS_ORDERS.ORDERS.EDIT')
          : $t('PRODUCTS_ORDERS.ORDERS.ADD')
      "
      :confirm-button-label="
        editingOrderId
          ? $t('PRODUCTS_ORDERS.ACTIONS.SAVE')
          : $t('PRODUCTS_ORDERS.ACTIONS.CREATE')
      "
      :is-loading="isOrderSubmitting"
      :disable-confirm-button="!canSubmitOrder"
      overflow-y-auto
      @confirm="submitOrder"
      @close="resetOrderForm"
    >
      <div class="grid gap-4">
        <div class="grid gap-4 md:grid-cols-2">
          <Input
            v-model="orderForm.reference"
            :label="$t('PRODUCTS_ORDERS.FIELDS.REFERENCE')"
          />
          <div class="flex min-w-0 flex-col gap-1">
            <label class="mb-0.5 text-heading-3 text-n-slate-12">
              {{ $t('PRODUCTS_ORDERS.FIELDS.PRODUCT') }}
            </label>
            <select
              v-model="orderForm.productId"
              class="h-10 rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-n-weak transition-all duration-200 hover:outline-n-slate-6 focus:outline-n-brand"
            >
              <option value="">
                {{ $t('PRODUCTS_ORDERS.LABELS.PRODUCT_NOT_SELECTED') }}
              </option>
              <option
                v-for="product in products"
                :key="product.id"
                :value="product.id"
              >
                {{ product.name }}
              </option>
            </select>
          </div>
          <div
            v-if="orderRequiresVariant"
            class="flex min-w-0 flex-col gap-1"
          >
            <label class="mb-0.5 text-heading-3 text-n-slate-12">
              {{ $t('PRODUCTS_ORDERS.FIELDS.VARIANT') }}
            </label>
            <select
              v-model="orderForm.productVariantId"
              class="h-10 rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-n-weak transition-all duration-200 hover:outline-n-slate-6 focus:outline-n-brand"
            >
              <option value="">
                {{ $t('PRODUCTS_ORDERS.QUICK_CREATE.SELECT_VARIANT') }}
              </option>
              <option
                v-for="variant in selectedProductVariants"
                :key="variant.id"
                :value="variant.id"
              >
                {{ variantDisplayName(variant) }}
              </option>
            </select>
          </div>
          <div class="space-y-2 md:col-span-2">
            <label class="mb-0.5 block text-heading-3 text-n-slate-12">
              {{ $t('PRODUCTS_ORDERS.FIELDS.CONTACT_NAME') }}
            </label>
            <div class="flex flex-col gap-2 lg:flex-row">
              <ComboBox
                v-model="selectedOrderContactId"
                :options="orderContactOptions"
                :placeholder="$t('PRODUCTS_ORDERS.ORDERS.SELECT_CONTACT')"
                :search-placeholder="$t('PRODUCTS_ORDERS.ORDERS.SEARCH_CONTACT')"
                :empty-state="$t('PRODUCTS_ORDERS.ORDERS.NO_CONTACTS_FOUND')"
                :message="
                  isSearchingOrderContacts
                    ? $t('SEARCH.LOADING_DATA')
                    : $t('PRODUCTS_ORDERS.ORDERS.CONTACT_SELECTOR_HELP')
                "
                use-api-results
                @search="debouncedLoadOrderContacts"
              />
              <Button
                variant="faded"
                color="slate"
                sm
                icon="i-lucide-user-plus"
                :label="$t('PRODUCTS_ORDERS.ORDERS.QUICK_ADD_CONTACT')"
                @click="openQuickOrderContactDialog"
              />
            </div>
          </div>
          <Input
            v-model="orderForm.contactName"
            :label="$t('PRODUCTS_ORDERS.FIELDS.CONTACT_NAME')"
            required
          />
          <Input
            v-model="orderForm.contactPhone"
            :label="$t('PRODUCTS_ORDERS.FIELDS.CONTACT_PHONE')"
          />
          <Input
            v-model="orderForm.contactEmail"
            type="email"
            :label="$t('PRODUCTS_ORDERS.FIELDS.CONTACT_EMAIL')"
          />
          <Input
            v-model="orderForm.quantity"
            type="number"
            min="1"
            :label="$t('PRODUCTS_ORDERS.FIELDS.QUANTITY')"
          />
          <Input
            v-model="orderForm.source"
            :label="$t('PRODUCTS_ORDERS.FIELDS.SOURCE')"
            required
          />
          <div class="flex min-w-0 flex-col gap-1">
            <label class="mb-0.5 text-heading-3 text-n-slate-12">
              {{ $t('PRODUCTS_ORDERS.FIELDS.STATUS') }}
            </label>
            <select
              v-model="orderForm.status"
              class="h-10 rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-n-weak transition-all duration-200 hover:outline-n-slate-6 focus:outline-n-brand"
            >
              <option
                v-for="option in orderStatusOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
          <Input
            v-model="orderForm.conversationId"
            type="number"
            min="1"
            :label="$t('PRODUCTS_ORDERS.FIELDS.CONVERSATION_ID')"
          />
          <div class="md:col-span-2">
            <p
              v-if="isFetchingConversationContext"
              class="mb-0 text-xs text-n-slate-10"
            >
              {{ $t('PRODUCTS_ORDERS.LABELS.LOADING_CONVERSATION_CONTEXT') }}
            </p>
            <p
              v-else-if="linkedConversationInbox"
              class="mb-0 text-xs text-n-slate-10"
            >
              {{
                $t('PRODUCTS_ORDERS.LABELS.TEMPLATES_FILTERED_BY_INBOX', {
                  inbox: linkedConversationInbox.name,
                })
              }}
            </p>
            <p
              v-else-if="conversationContextError"
              class="mb-0 text-xs text-n-ruby-11"
            >
              {{ conversationContextError }}
            </p>
            <p
              v-else
              class="mb-0 text-xs text-n-slate-10"
            >
              {{ $t('PRODUCTS_ORDERS.LABELS.ALL_INBOX_TEMPLATES') }}
            </p>
          </div>
          <div
            v-if="selectedProduct"
            class="md:col-span-2 rounded-xl border border-n-weak bg-n-alpha-1 p-4"
          >
            <div class="flex flex-wrap items-center gap-2">
              <p class="mb-0 text-sm font-medium text-n-slate-12">
                {{
                  selectedProductVariant
                    ? variantDisplayName(selectedProductVariant)
                    : selectedProduct.name
                }}
              </p>
              <span
                class="inline-flex rounded-full border px-2 py-1 text-xs font-semibold"
                :class="inventoryStatusClass(selectedOrderInventoryItem?.inventoryStatus)"
              >
                {{ inventoryStatusLabel(selectedOrderInventoryItem?.inventoryStatus) }}
              </span>
            </div>
            <p class="mb-0 mt-2 text-sm text-n-slate-11">
              {{
                orderTracksStock
                  ? $t('PRODUCTS_ORDERS.INVENTORY.AVAILABLE_STOCK', {
                      count: orderAvailableStock,
                    })
                  : $t('PRODUCTS_ORDERS.INVENTORY.NOT_TRACKED')
              }}
            </p>
            <p
              v-if="orderStockBlocked"
              class="mb-0 mt-2 text-sm text-n-ruby-11"
            >
              {{ $t('PRODUCTS_ORDERS.INVENTORY.OUT_OF_STOCK_BLOCKED') }}
            </p>
          </div>
          <Input
            v-model="orderForm.location"
            :label="$t('PRODUCTS_ORDERS.FIELDS.LOCATION')"
          />
        </div>

        <div class="grid gap-4 md:grid-cols-2">
          <Input
            v-model="orderForm.trackingNumber"
            :label="$t('PRODUCTS_ORDERS.FIELDS.TRACKING_NUMBER')"
          />
          <div
            v-if="paymentMethodOptions.length"
            class="flex min-w-0 flex-col gap-1"
          >
            <label class="mb-0.5 text-heading-3 text-n-slate-12">
              {{ $t('PRODUCTS_ORDERS.FIELDS.PAYMENT_METHOD') }}
            </label>
            <select
              v-model="orderForm.paymentMethod"
              class="h-10 rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-n-weak transition-all duration-200 hover:outline-n-slate-6 focus:outline-n-brand"
            >
              <option value="">
                {{ $t('PRODUCTS_ORDERS.PAYMENTS.SELECT_METHOD') }}
              </option>
              <option
                v-for="option in paymentMethodOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
          <Input
            v-else
            v-model="orderForm.paymentMethod"
            :label="$t('PRODUCTS_ORDERS.FIELDS.PAYMENT_METHOD')"
          />
          <div class="flex min-w-0 flex-col gap-1">
            <label class="mb-0.5 text-heading-3 text-n-slate-12">
              {{ $t('PRODUCTS_ORDERS.FIELDS.PAYMENT_STATUS') }}
            </label>
            <select
              v-model="orderForm.paymentStatus"
              class="h-10 rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-n-weak transition-all duration-200 hover:outline-n-slate-6 focus:outline-n-brand"
            >
              <option
                v-for="option in paymentStatusOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
          <Input
            v-model="orderForm.paymentProofUrl"
            :label="$t('PRODUCTS_ORDERS.FIELDS.PAYMENT_PROOF_URL')"
          />
          <Input
            v-model="orderForm.discountAmount"
            type="number"
            min="0"
            step="0.01"
            :label="$t('PRODUCTS_ORDERS.FIELDS.DISCOUNT_AMOUNT')"
          />
          <Input
            v-model="orderForm.shippingAmount"
            type="number"
            min="0"
            step="0.01"
            :label="$t('PRODUCTS_ORDERS.FIELDS.SHIPPING_AMOUNT')"
          />
          <Input
            v-model="orderForm.paidAmount"
            type="number"
            min="0"
            step="0.01"
            :label="$t('PRODUCTS_ORDERS.FIELDS.PAID_AMOUNT')"
          />
          <div class="flex min-w-0 flex-col gap-1">
            <label class="mb-0.5 text-heading-3 text-n-slate-12">
              {{ $t('PRODUCTS_ORDERS.FIELDS.RESPONSIBLE_AGENT') }}
            </label>
            <select
              v-model="orderForm.responsibleAgentId"
              class="h-10 rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-n-weak transition-all duration-200 hover:outline-n-slate-6 focus:outline-n-brand"
            >
              <option value="">
                {{ $t('PRODUCTS_ORDERS.FILTERS.ALL_AGENTS') }}
              </option>
              <option
                v-for="option in agentOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
        </div>

        <div class="grid gap-3 rounded-xl border border-n-weak bg-n-alpha-1 p-4 md:grid-cols-2 xl:grid-cols-4">
          <div class="rounded-lg bg-n-solid-1 p-3">
            <p class="mb-1 text-xs uppercase tracking-[0.08em] text-n-slate-10">
              {{ $t('PRODUCTS_ORDERS.FIELDS.SUBTOTAL_AMOUNT') }}
            </p>
            <p class="mb-0 text-sm font-semibold text-n-slate-12">
              {{ formatCurrencyAmount(orderSubtotalPreview, orderCurrency) }}
            </p>
          </div>
          <div class="rounded-lg bg-n-solid-1 p-3">
            <p class="mb-1 text-xs uppercase tracking-[0.08em] text-n-slate-10">
              {{ $t('PRODUCTS_ORDERS.FIELDS.TOTAL_AMOUNT') }}
            </p>
            <p class="mb-0 text-sm font-semibold text-n-slate-12">
              {{ formatCurrencyAmount(orderTotalPreview, orderCurrency) }}
            </p>
          </div>
          <div class="rounded-lg bg-n-solid-1 p-3">
            <p class="mb-1 text-xs uppercase tracking-[0.08em] text-n-slate-10">
              {{ $t('PRODUCTS_ORDERS.FIELDS.OUTSTANDING_AMOUNT') }}
            </p>
            <p class="mb-0 text-sm font-semibold text-n-slate-12">
              {{ formatCurrencyAmount(orderOutstandingPreview, orderCurrency) }}
            </p>
          </div>
          <div class="rounded-lg bg-n-solid-1 p-3">
            <p class="mb-1 text-xs uppercase tracking-[0.08em] text-n-slate-10">
              {{ $t('PRODUCTS_ORDERS.FIELDS.PAYMENT_STATUS') }}
            </p>
            <span
              class="inline-flex rounded-full border px-2 py-1 text-xs font-semibold"
              :class="paymentStatusBadgeClass(orderPaymentStatusPreview)"
            >
              {{ paymentStatusLabel(orderPaymentStatusPreview) }}
            </span>
          </div>
        </div>

        <TextArea
          v-model="orderForm.address"
          :label="$t('PRODUCTS_ORDERS.FIELDS.ADDRESS')"
          :max-length="1000"
        />

        <TextArea
          v-model="orderForm.notes"
          :label="$t('PRODUCTS_ORDERS.FIELDS.NOTES')"
          :max-length="3000"
        />

        <TextArea
          v-model="orderForm.fulfillmentNotes"
          :label="$t('PRODUCTS_ORDERS.FIELDS.FULFILLMENT_NOTES')"
          :max-length="3000"
        />

        <div class="rounded-xl border border-n-weak bg-n-alpha-1 p-4">
          <div class="mb-4 flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 class="mb-0 text-sm font-semibold text-n-slate-12">
                {{ $t('PRODUCTS_ORDERS.ACTIONS.TITLE') }}
              </h3>
              <p class="mb-0 text-xs text-n-slate-10">
                {{ $t('PRODUCTS_ORDERS.ORDERS.EMPTY_DESCRIPTION') }}
              </p>
            </div>
            <Button
              variant="faded"
              color="slate"
              sm
              icon="i-lucide-plus"
              :label="$t('PRODUCTS_ORDERS.ACTIONS.ADD')"
              @click="addActionRow"
            />
          </div>

          <div class="flex flex-col gap-4">
            <div
              v-for="(action, index) in orderForm.actions"
              :key="action.localKey"
              class="rounded-xl border border-n-weak bg-n-solid-1 p-4"
            >
              <div class="mb-3 flex items-center justify-between gap-2">
                <p class="mb-0 text-sm font-semibold text-n-slate-12">
                  {{
                    $t('PRODUCTS_ORDERS.LABELS.POSITION', {
                      count: index + 1,
                    })
                  }}
                </p>
                <Button
                  variant="ghost"
                  color="ruby"
                  sm
                  icon="i-lucide-trash-2"
                  :label="$t('PRODUCTS_ORDERS.ACTIONS.REMOVE')"
                  @click="removeActionRow(action)"
                />
              </div>

              <div class="grid gap-4 md:grid-cols-2">
                <Input
                  v-model="action.name"
                  :label="$t('PRODUCTS_ORDERS.ACTIONS.NAME')"
                  required
                />
                <div class="flex min-w-0 flex-col gap-1">
                  <label class="mb-0.5 text-heading-3 text-n-slate-12">
                    {{ $t('PRODUCTS_ORDERS.ACTIONS.TYPE') }}
                  </label>
                  <select
                    v-model="action.actionType"
                    class="h-10 rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-n-weak transition-all duration-200 hover:outline-n-slate-6 focus:outline-n-brand"
                  >
                    <option
                      v-for="option in actionTypeOptions"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                </div>
                <div class="flex min-w-0 flex-col gap-1">
                  <label class="mb-0.5 text-heading-3 text-n-slate-12">
                    {{ $t('PRODUCTS_ORDERS.ACTIONS.TRIGGER') }}
                  </label>
                  <select
                    v-model="action.triggerStatus"
                    class="h-10 rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-n-weak transition-all duration-200 hover:outline-n-slate-6 focus:outline-n-brand"
                  >
                    <option
                      v-for="option in actionTriggerOptions"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                </div>

                <TextArea
                  v-if="action.actionType !== 'template'"
                  v-model="action.content"
                  :label="$t('PRODUCTS_ORDERS.ACTIONS.CONTENT')"
                  :max-length="5000"
                  class="md:col-span-2"
                />
                <div
                  v-else
                  class="md:col-span-2"
                >
                  <label class="mb-0.5 block text-heading-3 text-n-slate-12">
                    {{ $t('PRODUCTS_ORDERS.ACTIONS.TEMPLATE') }}
                  </label>
                  <ComboBox
                    v-model="action.templateKey"
                    :options="templateOptionsForAction(action)"
                    :placeholder="$t('PRODUCTS_ORDERS.ACTIONS.SELECT_TEMPLATE')"
                    :empty-state="$t('PRODUCTS_ORDERS.ACTIONS.NO_TEMPLATES')"
                    @update:model-value="
                      value => handleTemplateSelection(action, value)
                    "
                  />
                  <p class="mt-2 mb-0 text-xs text-n-slate-10">
                    {{
                      linkedConversationInbox
                        ? $t('PRODUCTS_ORDERS.LABELS.TEMPLATES_FILTERED_BY_INBOX', {
                            inbox: linkedConversationInbox.name,
                          })
                        : $t('PRODUCTS_ORDERS.LABELS.ALL_INBOX_TEMPLATES')
                    }}
                  </p>
                  <div
                    v-if="selectedTemplateForAction(action)"
                    class="mt-3 space-y-3 rounded-xl border border-n-weak bg-n-alpha-1 p-3"
                  >
                    <div class="flex flex-wrap items-center justify-between gap-2">
                      <div class="text-xs text-n-slate-11">
                        {{ $t('PRODUCTS_ORDERS.ACTIONS.PREVIEW') }}
                      </div>
                      <div class="flex flex-wrap items-center gap-2">
                        <span class="rounded-full bg-n-solid-1 px-2 py-1 text-xs text-n-slate-11">
                          {{ $t('PRODUCTS_ORDERS.LABELS.INBOX') }}:
                          {{ selectedTemplateForAction(action).__inboxName }}
                        </span>
                        <Button
                          variant="ghost"
                          color="slate"
                          xs
                          icon="i-lucide-wand-sparkles"
                          :label="$t('PRODUCTS_ORDERS.ACTIONS.AUTOFILL')"
                          @click="autofillTemplateVariables(action)"
                        />
                      </div>
                    </div>
                    <WhatsAppTemplateParser
                      :ref="element => setTemplateParserRef(action.localKey, element)"
                      :template="selectedTemplateForAction(action)"
                    />
                  </div>
                </div>

                <label
                  class="flex items-center gap-2 text-sm font-medium text-n-slate-12 md:col-span-2"
                >
                  <input
                    v-model="action.completeOrder"
                    type="checkbox"
                    class="size-4 rounded border-n-weak text-n-brand focus:ring-n-brand"
                  />
                  {{ $t('PRODUCTS_ORDERS.ACTIONS.MARK_COMPLETE_ORDER') }}
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>

    <ProductOrderDetailDialog
      ref="orderDetailDialogRef"
      :orders="productOrders"
      :products-orders-features="productsOrdersFeatures"
      show-edit-action
      @edit="openOrderDialog"
      @advance="advanceOrder"
      @cancel="cancelOrder"
      @complete="completeOrder"
    />

    <CreateNewContactDialog
      ref="orderQuickContactDialogRef"
      @create="createQuickOrderContact"
    />
  </div>
</template>
